/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Button from "components/CustomButtons/Button.js";
import { Badge, Table, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/th";

// Chart Components
import { Bar, Doughnut, HorizontalBar } from "react-chartjs-2";

// Icons
import DashboardIcon from "@material-ui/icons/Dashboard";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import SchoolIcon from "@material-ui/icons/School";
import HotelIcon from "@material-ui/icons/Hotel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PeopleIcon from "@material-ui/icons/People";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import EventIcon from "@material-ui/icons/Event";
import BusinessIcon from "@material-ui/icons/Business";
import StorageIcon from "@material-ui/icons/Storage";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import SyncIcon from "@material-ui/icons/Sync";
import QueryBuilderIcon from "@material-ui/icons/QueryBuilder";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import WarningIcon from "@material-ui/icons/Warning";
import SearchIcon from "@material-ui/icons/Search";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewListIcon from "@material-ui/icons/ViewList";
import ScheduleIcon from "@material-ui/icons/Schedule";
import RoomIcon from "@material-ui/icons/Room";
import { checkTraineeManagePermission } from "./traineeAuth.js";
import {
  calculateDormFeeByDays,
  calculatePaymentBalance,
  THAI_FISCAL_MONTHS,
  DEFAULT_RATES,
  isStayActive
} from "./dormitoryFeeCalculator.js";

moment.locale("th");

// ฟังก์ชันคำนวณปีงบประมาณไทยปัจจุบัน (1 ต.ค. - 30 ก.ย.)
export const getThaiFiscalYear = (date = new Date()) => {
  const d = typeof date === "string" ? new Date(date) : (date || new Date());
  if (isNaN(d.getTime())) return new Date().getFullYear() + 543;
  const month = d.getMonth() + 1; // 1 - 12
  const yearCE = d.getFullYear();
  // เดือน 10, 11, 12 (1 ต.ค. เป็นต้นไป) นับเป็นปีงบประมาณถัดไป
  return month >= 10 ? yearCE + 543 + 1 : yearCE + 543;
};

// ฟังก์ชันสกัดอักษรย่อสำหรับ Avatar
export const getAvatarInitials = (name) => {
  if (!name) return "??";
  const clean = name
    .replace(/^(นพ\.|พญ\.|ดร\.|อาจารย์|นศ\.ภ\.|นศ\.|นาย|นางสาว|นาง|ว่าที่ ร\.ต\.|๑\.|๒\.|๓\.|๔\.|๕\.|๖\.)\s*/g, "")
    .trim();
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] || "") + (parts[1][0] || "");
  }
  return clean.slice(0, 2);
};

// ฟังก์ชันแปลงวันที่ไทยเพื่อนำมาคำนวณวันฝึก
export const parseThaiDateToObj = (str) => {
  if (!str) return null;
  const m1 = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m1) {
    let yr = parseInt(m1[3], 10);
    if (yr > 2400) yr -= 543;
    return new Date(yr, parseInt(m1[2], 10) - 1, parseInt(m1[1], 10));
  }
  const THAI_MONTHS = {
    "ม.ค.": 0, "ก.พ.": 1, "มี.ค.": 2, "เม.ย.": 3, "พ.ค.": 4, "มิ.ย.": 5,
    "ก.ค.": 6, "ส.ค.": 7, "ส.ย.": 8, "ก.ย.": 8, "ต.ค.": 9, "พ.ย.": 10, "ธ.ค.": 11
  };
  for (const [tName, tMonth] of Object.entries(THAI_MONTHS)) {
    if (str.includes(tName)) {
      const dayMatch = str.match(/\b(\d{1,2})\b/);
      const yearMatch = str.match(/\b(25\d{2}|\d{2})\b/);
      const day = dayMatch ? parseInt(dayMatch[1], 10) : 1;
      let yr = 2026;
      if (yearMatch) {
        let yVal = parseInt(yearMatch[1], 10);
        if (yVal < 100) yVal += 2500;
        yr = yVal - 543;
      }
      return new Date(yr, tMonth, day);
    }
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

// ฟังก์ชันคำนวณความคืบหน้าการฝึกและจำนวนวันคงเหลือ (Countdown)
export const calculateRotationProgress = (dateRangeText) => {
  const today = new Date();
  if (!dateRangeText || !dateRangeText.includes("-")) {
    return { percent: 100, elapsedDays: 0, remainingDays: 0, totalDays: 0 };
  }
  const parts = dateRangeText.split("-");
  const start = parseThaiDateToObj(parts[0].trim());
  const end = parseThaiDateToObj(parts[1].trim());
  if (!start || !end) {
    return { percent: 50, elapsedDays: 7, remainingDays: 7, totalDays: 14 };
  }
  const totalMs = end.getTime() - start.getTime();
  const totalDays = Math.max(1, Math.round(totalMs / (1000 * 60 * 60 * 24)));
  const elapsedMs = today.getTime() - start.getTime();
  const elapsedDays = Math.max(0, Math.min(totalDays, Math.round(elapsedMs / (1000 * 60 * 60 * 24))));
  const remainingMs = end.getTime() - today.getTime();
  const remainingDays = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)));
  const percent = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));
  return { percent, elapsedDays, remainingDays, totalDays };
};

// ฟังก์ชันสไตล์และสีประจำกลุ่มงาน (Department Theme)
export const getDepartmentTheme = (deptName = "", typeName = "") => {
  const isDoc = (typeName || "").includes("แพทย์");
  if (isDoc) {
    return {
      gradient: "linear-gradient(135deg, #0284c7, #0369a1)",
      borderAccent: "#0284c7",
      bgLight: "#f0f9ff",
      textDark: "#0369a1",
      badgeBg: "#e0f2fe",
      badgeColor: "#0369a1",
      badgeBorder: "#7dd3fc"
    };
  }
  if (deptName.includes("เภสัช")) {
    return {
      gradient: "linear-gradient(135deg, #10b981, #047857)",
      borderAccent: "#10b981",
      bgLight: "#f0fdf4",
      textDark: "#15803d",
      badgeBg: "#dcfce7",
      badgeColor: "#15803d",
      badgeBorder: "#86efac"
    };
  }
  if (deptName.includes("พยาธิ") || deptName.includes("แลป") || deptName.includes("ชันสูตร")) {
    return {
      gradient: "linear-gradient(135deg, #f59e0b, #b45309)",
      borderAccent: "#f59e0b",
      bgLight: "#fffbeb",
      textDark: "#b45309",
      badgeBg: "#fef3c7",
      badgeColor: "#b45309",
      badgeBorder: "#fde68a"
    };
  }
  if (deptName.includes("พยาบาล")) {
    return {
      gradient: "linear-gradient(135deg, #0d9488, #0f766e)",
      borderAccent: "#0d9488",
      bgLight: "#f0fdfa",
      textDark: "#0f766e",
      badgeBg: "#ccfbf1",
      badgeColor: "#0f766e",
      badgeBorder: "#99f6e4"
    };
  }
  return {
    gradient: "linear-gradient(135deg, #6366f1, #4338ca)",
    borderAccent: "#6366f1",
    bgLight: "#eef2ff",
    textDark: "#4338ca",
    badgeBg: "#e0e7ff",
    badgeColor: "#4338ca",
    badgeBorder: "#c7d2fe"
  };
};

const useStyles = makeStyles({
  container: {
    padding: "20px 24px",
    fontFamily: '"Sarabun", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: "#f4f6f9",
    minHeight: "100vh",
    color: "#0f172a" // สีตัวหนังสือหลัก คมชัด เข้ม ชัดเจน
  },
  headerBanner: {
    background: "linear-gradient(135deg, #091e3a 0%, #112d4e 60%, #064e3b 100%)",
    borderRadius: "16px",
    padding: "24px 28px",
    color: "#ffffff",
    marginBottom: "24px",
    boxShadow: "0 10px 25px -5px rgba(9, 30, 58, 0.3)"
  },
  liveBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "rgba(16, 185, 129, 0.25)",
    color: "#ffffff",
    border: "1px solid #10b981",
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "0.8rem",
    fontWeight: "700",
    marginRight: "10px"
  },
  liveDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#34d399",
    marginRight: "7px",
    boxShadow: "0 0 10px #34d399"
  },
  yearPill: {
    cursor: "pointer",
    padding: "6px 18px",
    borderRadius: "20px",
    fontSize: "0.9rem",
    fontWeight: "700",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    marginRight: "8px",
    display: "inline-flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      borderColor: "#ffffff"
    }
  },
  yearPillActive: {
    backgroundColor: "#ffffff !important",
    color: "#091e3a !important",
    borderColor: "#ffffff !important",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.2)",
    fontWeight: "800"
  },
  // Modern High-Contrast Stat Card
  statCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #cbd5e1",
    padding: "22px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    marginBottom: "22px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "190px",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 12px 28px -4px rgba(0, 0, 0, 0.1)",
      borderColor: "#94a3b8"
    }
  },
  iconBoxTeal: {
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    backgroundColor: "#e0f2fe",
    border: "1px solid #38bdf8",
    color: "#0369a1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  iconBoxGreen: {
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    backgroundColor: "#dcfce7",
    border: "1px solid #4ade80",
    color: "#15803d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  iconBoxPurple: {
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    backgroundColor: "#f3e8ff",
    border: "1px solid #c084fc",
    color: "#6b21a8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  iconBoxOrange: {
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    backgroundColor: "#ffedd5",
    border: "1px solid #fb923c",
    color: "#c2410c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  // ป้ายข้อความย่อย คมชัด มองเห็นง่าย (High Contrast)
  miniPillDark: {
    fontSize: "0.82rem",
    fontWeight: "700",
    padding: "4px 10px",
    borderRadius: "8px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #cbd5e1",
    color: "#0f172a",
    display: "inline-flex",
    alignItems: "center"
  },
  cardDivider: {
    height: "1px",
    backgroundColor: "#e2e8f0",
    margin: "14px 0 10px 0"
  },
  // Section Modern Card
  modernCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #cbd5e1",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.04)",
    marginBottom: "24px",
    overflow: "hidden"
  },
  modernCardHeader: {
    padding: "18px 24px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap"
  },
  cardAccentBorderOrange: {
    borderLeft: "5px solid #ea580c"
  },
  cardAccentBorderTeal: {
    borderLeft: "5px solid #0284c7"
  },
  cardAccentBorderIndigo: {
    borderLeft: "5px solid #4f46e5"
  },
  cardAccentBorderAmber: {
    borderLeft: "5px solid #d97706"
  },
  // Badge ห้องพัก คมชัด อ่านง่ายมาก (High Contrast)
  roomBadgeDorm: {
    backgroundColor: "#e0f2fe",
    color: "#0369a1",
    border: "1px solid #7dd3fc",
    fontWeight: "700",
    padding: "5px 12px",
    borderRadius: "8px",
    fontSize: "0.86rem",
    display: "inline-flex",
    alignItems: "center"
  },
  roomBadgeNone: {
    backgroundColor: "#f1f5f9",
    color: "#334155",
    border: "1px solid #cbd5e1",
    fontWeight: "600",
    padding: "5px 12px",
    borderRadius: "8px",
    fontSize: "0.86rem",
    display: "inline-flex",
    alignItems: "center"
  },
  // กล่องแสดงห้องพักรายตึก (ตึก/ชั้น/ห้อง)
  buildingPanel: {
    backgroundColor: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    marginBottom: "20px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
  },
  buildingPanelHeader: {
    padding: "14px 20px",
    backgroundColor: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  floorHeader: {
    backgroundColor: "#f1f5f9",
    padding: "8px 16px",
    fontWeight: "700",
    fontSize: "0.88rem",
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center"
  },
  roomItemCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "12px 14px",
    margin: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#0284c7",
      boxShadow: "0 4px 12px rgba(2, 132, 199, 0.1)"
    }
  },
  actionNavCard: {
    cursor: "pointer",
    backgroundColor: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: "16px",
    padding: "20px 22px",
    transition: "all 0.25s ease",
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.04)",
    "&:hover": {
      borderColor: "#0284c7",
      boxShadow: "0 10px 24px rgba(2, 132, 199, 0.15)",
      transform: "translateY(-3px)"
    }
  },
  "@keyframes livePulseGlow": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.7)"
    },
    "70%": {
      boxShadow: "0 0 0 8px rgba(34, 197, 94, 0)"
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(34, 197, 94, 0)"
    }
  },
  activeLiveDot: {
    width: "10px",
    height: "10px",
    backgroundColor: "#22c55e",
    borderRadius: "50%",
    display: "inline-block",
    animation: "$livePulseGlow 2s infinite",
    marginRight: "6px"
  },
  traineeCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.05)",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "hidden",
    height: "100%",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 14px 28px -4px rgba(15, 23, 42, 0.12)",
      borderColor: "#cbd5e1"
    }
  }
});

// ข้อมูลจำลองหอพัก 4 ตึก (ตึกละ 3 ชั้น) - เน้นเฉพาะห้องที่มีคนพัก ไม่บอกว่าเหลืออีกกี่ห้อง
const DORM_OCCUPIED_BY_BUILDINGS = [
  {
    buildingNo: "1",
    buildingName: "อาคาร 1 (หอพักนักศึกษาฝึกงาน)",
    primaryGroup: "นักศึกษาเภสัชกรรม / พยาบาล",
    residentsCount: 6,
    floors: [
      {
        floorNo: 1,
        floorTitle: "ชั้น 1",
        rooms: [
          {
            roomNumber: "111",
            traineeType: "STUDENT",
            residentName: "นศ.ภ. ศศิธร เจริญผล",
            roleTitle: "นักศึกษาเภสัชกรรม (ม.อ.)",
            department: "กลุ่มงานเภสัชกรรม",
            dateRange: "1 ต.ค. 68 - 5 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: "ใกล้ครบกำหนดฝึก 5 ธ.ค."
          }
        ]
      },
      {
        floorNo: 2,
        floorTitle: "ชั้น 2",
        rooms: [
          {
            roomNumber: "121",
            traineeType: "STUDENT",
            residentName: "นศ.ภ. เมธาวี จิตเที่ยง",
            roleTitle: "นักศึกษาเภสัชกรรม (ม.อ.)",
            department: "กลุ่มงานเภสัชกรรม",
            dateRange: "1 ต.ค. 68 - 30 พ.ย. 68",
            status: "กำลังพักอาศัย",
            note: ""
          },
          {
            roomNumber: "122",
            traineeType: "STUDENT",
            residentName: "นศ.ภ. ภัทรพล วงศ์สว่าง",
            roleTitle: "นักศึกษาเภสัชกรรม (ม.อ.)",
            department: "กลุ่มงานเภสัชกรรม",
            dateRange: "1 ต.ค. 68 - 30 พ.ย. 68",
            status: "กำลังพักอาศัย",
            note: "🔔 จัดสรรรอบถัดไป: นศ.ภ. ปริญญา มหิดล (เริ่ม 1 ธ.ค. 68)"
          }
        ]
      }
    ]
  },
  {
    buildingNo: "2",
    buildingName: "อาคาร 2 (หอพักแพทย์)",
    primaryGroup: "แพทย์ Fellow ตจวิทยา / Elective",
    residentsCount: 6,
    floors: [
      {
        floorNo: 2,
        floorTitle: "ชั้น 2",
        rooms: [
          {
            roomNumber: "221",
            traineeType: "DOCTOR",
            residentName: "พญ.กานดา รัตนโชติ",
            roleTitle: "แพทย์ Fellow ตจวิทยา (สถาบันโรคผิวหนัง)",
            department: "กลุ่มงานอายุรกรรมผิวหนัง",
            dateRange: "1 ส.ค. 68 - 31 ก.ค. 69",
            status: "กำลังพักอาศัย",
            note: ""
          },
          {
            roomNumber: "222",
            traineeType: "DOCTOR",
            residentName: "นพ.สิทธิชัย อัศวรักษ์",
            roleTitle: "แพทย์ Fellow ตจวิทยา (คณะแพทยศาสตร์ ม.อ.)",
            department: "กลุ่มงานอายุรกรรมผิวหนัง",
            dateRange: "1 ส.ค. 68 - 31 ก.ค. 69",
            status: "กำลังพักอาศัย",
            note: ""
          }
        ]
      },
      {
        floorNo: 3,
        floorTitle: "ชั้น 3",
        rooms: [
          {
            roomNumber: "231",
            traineeType: "DOCTOR",
            residentName: "พญ.ศิริพร บุญยืน",
            roleTitle: "แพทย์ Elective ตจวิทยา (รพ.สงขลานครินทร์)",
            department: "กลุ่มงานศัลยกรรมเลเซอร์",
            dateRange: "1 ต.ค. 68 - 31 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: ""
          },
          {
            roomNumber: "232",
            traineeType: "DOCTOR",
            residentName: "นพ.ณัฐวุฒิ สุขสวัสดิ์",
            roleTitle: "แพทย์ Elective ตจวิทยา (รพ.วชิระภูเก็ต)",
            department: "กลุ่มงานศัลยกรรมเลเซอร์",
            dateRange: "1 ต.ค. 68 - 30 พ.ย. 68",
            status: "กำลังพักอาศัย",
            note: ""
          },
          {
            roomNumber: "233",
            traineeType: "DOCTOR",
            residentName: "พญ.วิมลมาศ สุวรรณ & นพ.ธีรภัทร",
            roleTitle: "แพทย์เพิ่มพูนทักษะ Elective (รพ.หาดใหญ่)",
            department: "กลุ่มงานอายุรกรรมผิวหนัง",
            dateRange: "15 ธ.ค. 68 - 15 ม.ค. 69",
            status: "จัดสรรล่วงหน้า",
            note: "🔔 สำรองรอบถัดไป (เริ่ม 15 ธ.ค. 68)"
          }
        ]
      }
    ]
  },
  {
    buildingNo: "3",
    buildingName: "อาคาร 3 (หอพักบุคลากร / นศ.หญิง)",
    primaryGroup: "นักศึกษาพยาบาลหญิง / พยาบาลเฉพาะทาง",
    residentsCount: 4,
    floors: [
      {
        floorNo: 1,
        floorTitle: "ชั้น 1",
        rooms: [
          {
            roomNumber: "311",
            traineeType: "STUDENT",
            residentName: "น.ส.วรรณิศา สมบัติ",
            roleTitle: "นักศึกษาพยาบาล (วพบ.ตรัง)",
            department: "กลุ่มงานการพยาบาล",
            dateRange: "15 ต.ค. 68 - 15 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: ""
          },
          {
            roomNumber: "312",
            traineeType: "STUDENT",
            residentName: "น.ส.ปวีณา บุญชู",
            roleTitle: "นักศึกษาพยาบาล (วพบ.ตรัง)",
            department: "กลุ่มงานการพยาบาล",
            dateRange: "15 ต.ค. 68 - 15 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: ""
          }
        ]
      },
      {
        floorNo: 2,
        floorTitle: "ชั้น 2",
        rooms: [
          {
            roomNumber: "321",
            traineeType: "STUDENT",
            residentName: "พว.ชลธิชา แก้วมี",
            roleTitle: "พยาบาลฝึกอบรมเฉพาะทางผิวหนัง",
            department: "กลุ่มงานการพยาบาล",
            dateRange: "1 ก.ย. 68 - 31 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: ""
          }
        ]
      }
    ]
  },
  {
    buildingNo: "4",
    buildingName: "อาคาร 4 (หอพักรับรองพิเศษ)",
    primaryGroup: "อาจารย์แพทย์ / แพทย์รับรอง",
    residentsCount: 2,
    floors: [
      {
        floorNo: 1,
        floorTitle: "ชั้น 1",
        rooms: [
          {
            roomNumber: "411",
            traineeType: "DOCTOR",
            residentName: "นพ.ธีรภัทร วัฒนากุล",
            roleTitle: "แพทย์เพิ่มพูนทักษะ",
            department: "กลุ่มงานอายุรกรรมผิวหนัง",
            dateRange: "1 ต.ค. 68 - 31 ม.ค. 69",
            status: "กำลังพักอาศัย",
            note: ""
          }
        ]
      },
      {
        floorNo: 2,
        floorTitle: "ชั้น 2",
        rooms: [
          {
            roomNumber: "421",
            traineeType: "DOCTOR",
            residentName: "รศ.พญ.นภา สุขุมวิท",
            roleTitle: "อาจารย์แพทย์ผู้เชี่ยวชาญพิเศษ",
            department: "ศูนย์วิจัยและฝึกอบรม",
            dateRange: "15 พ.ย. 68 - 15 ธ.ค. 68",
            status: "กำลังพักอาศัย",
            note: ""
          }
        ]
      }
    ]
  }
];

// โครงสร้างข้อมูลสถิติเริ่มต้น (ดึงจากฐานข้อมูลจริง 100%)
const DASHBOARD_DATA = {
  fiscalYear: 2570,
  currentlyActive: {
    total: 0,
    doctors: 0,
    students: 0,
    studentsBatchSum: 0,
    fellow: 0,
    elective: 0,
    pharmacy: 0,
    nursing: 0,
    lab: 0,
    it: 0,
    withDorm: 0,
    withoutDorm: 0,
    cancelled: 0,
    completed: 0
  },
  overall: {
    doctors: { total: 0, fellow: 0, elective: 0, active: 0, upcoming: 0, completed: 0, cancelled: 0 },
    students: { total: 0, records: 0, active: 0, upcoming: 0, completed: 0, cancelled: 0, pharmacy: 0, nursing: 0, lab: 0, it: 0 },
    totalCancelled: 0,
    totalCompleted: 0
  },
  upcomingIntake: [],
  endingSoon: [],
  currentTraineesRoster: [],
  dormitory: {
    totalRooms: 41,
    occupiedRooms: 0,
    availableRooms: 41,
    maintenanceRooms: 0,
    totalBeds: 82,
    occupiedBeds: 0,
    availableBeds: 82,
    occupancyRate: 0,
    pendingPayments: 0,
    pendingPaymentAmount: 0
  },
  departmentBreakdown: [],
  monthlyTrends: {
    months: ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."],
    doctors: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    students: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  }
};

const FINANCIAL_SUMMARY_DATA = {
  fiscalYear: 2570,
  totalExpectedRevenue: 0,
  totalCollected: 0,
  totalOverdue: 0,
  collectionRate: 0,
  totalResidentsCount: 0,
  overdueResidentsCount: 0,
  fullyPaidCount: 0,
  buildingStats: [],
  monthlyCollectionTrends: {
    months: ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."],
    expected: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    collected: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  },
  residentPayments: []
};

export default function TraineeDashboard() {
  const classes = useStyles();
  const history = useHistory();

  // ค่าเริ่มต้นปีงบประมาณไทยปัจจุบัน (1 ต.ค. - 30 ก.ย.)
  const currentFiscalYear = useMemo(() => getThaiFiscalYear(), []);
  const [selectedYear, setSelectedYear] = useState(() => getThaiFiscalYear());

  // ตัวเลือกปีงบประมาณในแถบเมนู (ครอบคลุมปีปัจจุบัน และปีก่อนหน้า/ถัดไป)
  const fiscalYearOptions = useMemo(() => {
    const years = new Set([
      2568,
      2569,
      2570,
      currentFiscalYear - 2,
      currentFiscalYear - 1,
      currentFiscalYear,
      currentFiscalYear + 1
    ]);
    return Array.from(years).sort((a, b) => a - b);
  }, [currentFiscalYear]);
  const SHOW_FINANCIAL_TAB = false; // ซ่อนแท็บ 2. Dashboard การเงินหอพัก (ยอดเก็บ) ไปก่อนตามคำขอผู้ใช้
  const [dashboardMode, setDashboardMode] = useState("OPERATIONAL"); // "OPERATIONAL" or "FINANCIAL"
  const [financialData, setFinancialData] = useState(FINANCIAL_SUMMARY_DATA);
  const [activeViewTab, setActiveViewTab] = useState(0); // 0 = Dept Summary, 1 = Trainee Roster

  // State สำหรับแผงแสดงผู้ฝึกปฏิบัติงานปัจจุบัน (Active Trainees Showcase)
  const [activeRosterSearch, setActiveRosterSearch] = useState("");
  const [activeRosterTypeFilter, setActiveRosterTypeFilter] = useState("ALL"); // "ALL", "DOCTOR", "STUDENT", "DORM", "NONDORM"
  const [activeRosterViewMode, setActiveRosterViewMode] = useState("GRID"); // "GRID" or "TABLE"

  const [selectedBuildingTab, setSelectedBuildingTab] = useState("ALL"); // ALL, 1, 2, 3, 4
  const [dormStatusFilter, setDormStatusFilter] = useState("ACTIVE"); // "ACTIVE" (กำลังพักปัจจุบัน), "PAST" (ที่ผ่านมาแล้ว/ประวัติ), "ALL" (ทั้งหมด)
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(DASHBOARD_DATA);
  const [canManage, setCanManage] = useState(false);
  const [rawDoctors, setRawDoctors] = useState([]);
  const [rawStudents, setRawStudents] = useState([]);
  const [chartDormView, setChartDormView] = useState("BUILDING"); // "BUILDING" vs "ROLE"
  const [chartStatusFilter, setChartStatusFilter] = useState("ACTIVE"); // "ACTIVE" (กำลังฝึกอยู่ปัจจุบัน), "ALL" (ทั้งหมด)
  const isMountedRef = React.useRef(true);

  const allActiveTrainees = useMemo(() => {
    if (Array.isArray(dashboardData?.currentTraineesRoster) && dashboardData.currentTraineesRoster.length > 0) {
      return dashboardData.currentTraineesRoster;
    }
    // เฉพาะปีงบประมาณปัจจุบัน (currentFiscalYear) เท่านั้น ที่จะใช้ Fallback หาก API คืนค่าว่าง
    if (selectedYear === currentFiscalYear) {
      if (Array.isArray(DASHBOARD_DATA?.currentTraineesRoster) && DASHBOARD_DATA.currentTraineesRoster.length > 0) {
        return DASHBOARD_DATA.currentTraineesRoster;
      }
    }
    return [];
  }, [dashboardData?.currentTraineesRoster, selectedYear, currentFiscalYear]);

  const filteredActiveTrainees = useMemo(() => {
    return allActiveTrainees.filter((t) => {
      if (activeRosterSearch) {
        const q = activeRosterSearch.toLowerCase().trim();
        const matchName = (t.name || "").toLowerCase().includes(q);
        const matchDept = (t.department || "").toLowerCase().includes(q);
        const matchInst = (t.institution || "").toLowerCase().includes(q);
        const matchType = (t.type || "").toLowerCase().includes(q);
        const matchRoom = (t.dormRoom || "").toLowerCase().includes(q);
        if (!matchName && !matchDept && !matchInst && !matchType && !matchRoom) return false;
      }
      if (activeRosterTypeFilter === "DOCTOR") {
        return (t.type || "").includes("แพทย์");
      }
      if (activeRosterTypeFilter === "STUDENT") {
        return (t.type || "").includes("นักศึกษา") || (t.type || "").includes("นศ.");
      }
      if (activeRosterTypeFilter === "DORM") {
        return t.hasDorm === true;
      }
      if (activeRosterTypeFilter === "NONDORM") {
        return !t.hasDorm;
      }
      return true;
    });
  }, [allActiveTrainees, activeRosterSearch, activeRosterTypeFilter]);

  const activeRosterCounts = useMemo(() => {
    return {
      all: allActiveTrainees.length,
      doctors: allActiveTrainees.filter(t => (t.type || "").includes("แพทย์")).length,
      students: allActiveTrainees.filter(t => (t.type || "").includes("นักศึกษา") || (t.type || "").includes("นศ.")).length,
      dorm: allActiveTrainees.filter(t => t.hasDorm === true).length,
      nonDorm: allActiveTrainees.filter(t => !t.hasDorm).length
    };
  }, [allActiveTrainees]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Live Calculator Simulator State for Financial Dashboard
  const [simBuilding, setSimBuilding] = useState("ตึก 1");
  const [simMonths, setSimMonths] = useState(1);
  const [simWeeks, setSimWeeks] = useState(1);
  const [simDays, setSimDays] = useState(0);
  const [simDoctorDays, setSimDoctorDays] = useState(14);
  const [simPaidAmount, setSimPaidAmount] = useState(500);

  // Payment Recording Modal State
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedStayForPayment, setSelectedStayForPayment] = useState(null);
  const [paymentInput, setPaymentInput] = useState({
    feeAmount: 500,
    paidAmount: 0,
    remainingBalance: 0,
    paymentStatus: "PAID",
    receiptNo: "",
    paidDate: new Date().toISOString().split("T")[0],
    note: ""
  });

  const handleOpenPaymentModal = (item) => {
    if (!item) return;
    setSelectedStayForPayment(item);
    const fee = Number(item.dueAmount ?? item.feeAmount) >= 0 ? Number(item.dueAmount ?? item.feeAmount) : 500;
    const paid = Number(item.paidAmount) || 0;
    const rem = Number(item.overdueAmount ?? item.remainingBalance) >= 0 ? Number(item.overdueAmount ?? item.remainingBalance) : Math.max(0, fee - paid);
    const status = rem === 0 && fee > 0 ? "PAID" : (paid > 0 ? "PARTIAL" : "OVERDUE");

    setPaymentInput({
      feeAmount: fee,
      paidAmount: paid,
      remainingBalance: rem,
      paymentStatus: status,
      receiptNo: item.receiptNo || `REC-2569-${Math.floor(1000 + Math.random() * 9000)}`,
      paidDate: item.paidDate ? String(item.paidDate).split("T")[0] : new Date().toISOString().split("T")[0],
      note: item.remarks || (item.statusText && !item.statusText.startsWith("ค้าง") ? item.statusText : "")
    });
    setPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!selectedStayForPayment) return;
    const fee = Number(paymentInput.feeAmount) >= 0 ? Number(paymentInput.feeAmount) : 500;
    const paid = Math.max(0, Number(paymentInput.paidAmount) || 0);
    const remaining = Math.max(0, fee - paid);
    
    let newStatus = paymentInput.paymentStatus;
    if (!newStatus) {
      if (remaining === 0) newStatus = "PAID";
      else if (paid > 0) newStatus = "PARTIAL";
      else newStatus = "OVERDUE";
    }

    try {
      if (selectedStayForPayment.id) {
        await axios.post(`/api/v1/dormitory/stays/${selectedStayForPayment.id}/record-payment`, {
          feeAmount: fee,
          amountPaid: 0,
          totalPaidAmount: paid,
          receiptNo: paymentInput.receiptNo,
          paidDate: paymentInput.paidDate,
          remarks: paymentInput.note,
          paymentStatus: newStatus
        });
      }
      fetchDashboardData();
    } catch (err) {
      console.warn("Could not persist payment from TraineeDashboard, updating state", err);
    }

    // Update local state in financialData
    setFinancialData((prev) => {
      if (!prev) return prev;
      const updatedQueue = (prev.overdueQueue || []).map((q) => {
        if (q.id === selectedStayForPayment.id || q.name === selectedStayForPayment.name) {
          return {
            ...q,
            dueAmount: fee,
            paidAmount: paid,
            overdueAmount: remaining,
            statusText: newStatus === "PAID" ? "ชำระครบถ้วนแล้ว" : (paid > 0 ? `จ่ายแล้ว ${paid.toLocaleString()} บ. (ค้าง ${remaining.toLocaleString()} บ.)` : `ค้างจ่าย ${remaining.toLocaleString()} บาท`),
            statusBadgeColor: newStatus === "PAID" ? "success" : (paid > 0 ? "warning" : "danger")
          };
        }
        return q;
      }).filter(q => q.overdueAmount > 0);

      const updatedPayments = (prev.residentPayments || []).map((p) => {
        if (p.id === selectedStayForPayment.id || p.traineeName === selectedStayForPayment.name) {
          return {
            ...p,
            feeAmount: fee,
            paidAmount: paid,
            remainingBalance: remaining,
            paymentStatus: newStatus,
            receiptNo: paymentInput.receiptNo,
            paidDate: paymentInput.paidDate,
            remarks: paymentInput.note
          };
        }
        return p;
      });

      return {
        ...prev,
        overdueQueue: updatedQueue,
        residentPayments: updatedPayments
      };
    });

    setPaymentModal(false);
  };

  // Resident & Room Payment Board State
  const [paymentRoomFilter, setPaymentRoomFilter] = useState("ALL"); // ALL, PAID, OVERDUE, B1, B2, B4
  const [paymentSearch, setPaymentSearch] = useState("");

  const residentPaymentsSource = useMemo(() => {
    if (financialData && Array.isArray(financialData.residentPayments)) {
      return financialData.residentPayments;
    }
    return FINANCIAL_SUMMARY_DATA.residentPayments || [];
  }, [financialData]);

  const residentFilterCounts = useMemo(() => {
    const all = residentPaymentsSource;
    const paid = all.filter(item => item.paymentStatus === "PAID" || (item.remainingBalance || 0) <= 0).length;
    const overdue = all.filter(item => item.paymentStatus === "OVERDUE" || (item.remainingBalance || 0) > 0).length;
    const b1 = all.filter(item => item.buildingNo === "1").length;
    const b2 = all.filter(item => item.buildingNo === "2").length;
    const b4 = all.filter(item => item.buildingNo === "4").length;
    return { all: all.length, paid, overdue, b1, b2, b4 };
  }, [residentPaymentsSource]);

  const residentPaymentList = useMemo(() => {
    return residentPaymentsSource.filter(item => {
      // Status / Building Filter
      if (paymentRoomFilter === "PAID" && item.paymentStatus !== "PAID" && (item.remainingBalance || 0) > 0) return false;
      if (paymentRoomFilter === "OVERDUE" && item.paymentStatus !== "OVERDUE" && (item.remainingBalance || 0) <= 0) return false;
      if (paymentRoomFilter === "B1" && item.buildingNo !== "1") return false;
      if (paymentRoomFilter === "B2" && item.buildingNo !== "2") return false;
      if (paymentRoomFilter === "B4" && item.buildingNo !== "4") return false;

      // Search query
      if (paymentSearch && paymentSearch.trim()) {
        const q = paymentSearch.trim().toLowerCase();
        const matchName = (item.traineeName || "").toLowerCase().includes(q);
        const matchRoom = (item.roomNumber || "").toLowerCase().includes(q);
        const matchInst = (item.institution || "").toLowerCase().includes(q);
        const matchDept = (item.department || "").toLowerCase().includes(q);
        const matchRole = (item.role || "").toLowerCase().includes(q);
        const matchFormula = (item.prorationFormula || "").toLowerCase().includes(q);
        if (!matchName && !matchRoom && !matchInst && !matchDept && !matchRole && !matchFormula) return false;
      }
      return true;
    });
  }, [residentPaymentsSource, paymentRoomFilter, paymentSearch]);

  const filteredPaymentTotals = useMemo(() => {
    const expected = residentPaymentList.reduce((acc, cur) => acc + (Number(cur.feeAmount) || 0), 0);
    const paid = residentPaymentList.reduce((acc, cur) => acc + (Number(cur.paidAmount) || 0), 0);
    const overdue = residentPaymentList.reduce((acc, cur) => acc + (Number(cur.remainingBalance) || 0), 0);
    const rate = expected > 0 ? Math.round((paid / expected) * 1000) / 10 : 0;
    return { expected, paid, overdue, rate, count: residentPaymentList.length };
  }, [residentPaymentList]);

  // สรุปสถิติประจำปีงบประมาณที่เลือกสำหรับ Operational Dashboard
  const statsSummary = useMemo(() => {
    const act = dashboardData?.currentlyActive || {};
    const ovr = dashboardData?.overall || {};
    const docOvr = ovr.doctors || {};
    const stuOvr = ovr.students || {};

    const totalTrainees = ovr.totalTrainees ?? ((docOvr.total || 0) + (stuOvr.total || 0));
    const activeTotal = act.total ?? 0;
    const activeDocs = act.doctors ?? 0;
    const activeStus = act.students ?? 0;
    const upcomingTotal = ovr.totalUpcoming ?? ((docOvr.upcoming || 0) + (stuOvr.upcoming || 0));
    const upcomingDocs = docOvr.upcoming ?? 0;
    const upcomingStus = stuOvr.upcoming ?? 0;
    const completedTotal = ovr.totalCompleted ?? (act.completed ?? 0);
    const completedDocs = docOvr.completed ?? (act.completedDoctors ?? 0);
    const completedStus = stuOvr.completed ?? (act.completedStudents ?? 0);
    const cancelledTotal = ovr.totalCancelled ?? (act.cancelled ?? 0);
    const cancelledDocs = docOvr.cancelled ?? (act.cancelledDoctors ?? 0);
    const cancelledStus = stuOvr.cancelled ?? (act.cancelledStudents ?? 0);

    return {
      totalTrainees,
      activeTotal,
      activeDocs,
      activeStus,
      upcomingTotal,
      upcomingDocs,
      upcomingStus,
      completedTotal,
      completedDocs,
      completedStus,
      cancelledTotal,
      cancelledDocs,
      cancelledStus,
      docTotal: docOvr.total ?? activeDocs,
      docFellow: docOvr.fellow ?? (act.fellow ?? 0),
      docElective: docOvr.elective ?? (act.elective ?? 0),
      stuTotal: stuOvr.total ?? activeStus,
      stuPharmacy: stuOvr.pharmacy ?? (act.pharmacy ?? 0),
      stuNursing: stuOvr.nursing ?? (act.nursing ?? 0),
      stuLab: stuOvr.lab ?? (act.lab ?? 0),
      stuIT: stuOvr.it ?? (act.it ?? 0),
      withDorm: act.withDorm ?? (financialData?.totalResidentsCount ?? 0),
      withoutDorm: act.withoutDorm ?? 0
    };
  }, [dashboardData, financialData]);

  useEffect(() => {
    setCanManage(checkTraineeManagePermission());
  }, []);

  // Helper: Format and sanitize Operational Dashboard payload
  const formatDashboardPayload = (payload, year) => {
    if (!payload) return getMockDashboardByYear(year);
    return {
      ...DASHBOARD_DATA,
      ...payload,
      fiscalYear: payload.fiscalYear || year,
      currentlyActive: {
        total: 0,
        doctors: 0,
        students: 0,
        studentsBatchSum: 0,
        fellow: 0,
        elective: 0,
        pharmacy: 0,
        nursing: 0,
        lab: 0,
        it: 0,
        withDorm: 0,
        withoutDorm: 0,
        completed: 0,
        cancelled: 0,
        ...(payload.currentlyActive || {})
      },
      overall: {
        ...DASHBOARD_DATA.overall,
        ...(payload.overall || {})
      },
      dormitory: {
        ...DASHBOARD_DATA.dormitory,
        ...(payload.dormitory || {})
      },
      upcomingIntake: Array.isArray(payload.upcomingIntake) ? payload.upcomingIntake : [],
      endingSoon: Array.isArray(payload.endingSoon) ? payload.endingSoon : [],
      currentTraineesRoster: Array.isArray(payload.currentTraineesRoster) ? payload.currentTraineesRoster : [],
      departmentBreakdown: Array.isArray(payload.departmentBreakdown) ? payload.departmentBreakdown : [],
      monthlyTrends: (payload.monthlyTrends && Array.isArray(payload.monthlyTrends.months))
        ? payload.monthlyTrends
        : { months: ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."], doctors: [0,0,0,0,0,0,0,0,0,0,0,0], students: [0,0,0,0,0,0,0,0,0,0,0,0] }
    };
  };

  // Helper: Format and sanitize Financial Dashboard payload
  const formatFinancialPayload = (payload, year) => {
    if (!payload) return getMockFinancialByYear(year);
    return {
      ...FINANCIAL_SUMMARY_DATA,
      ...payload,
      fiscalYear: payload.fiscalYear || year,
      buildingStats: Array.isArray(payload.buildingStats) ? payload.buildingStats : (FINANCIAL_SUMMARY_DATA.buildingStats || []),
      overdueQueue: Array.isArray(payload.overdueQueue) ? payload.overdueQueue : [],
      monthlyCashFlow: Array.isArray(payload.monthlyCashFlow) ? payload.monthlyCashFlow : (FINANCIAL_SUMMARY_DATA.monthlyCashFlow || []),
      residentPayments: Array.isArray(payload.residentPayments) ? payload.residentPayments : []
    };
  };

    // Default Data Helper by Fiscal Year (Empty state when API is unavailable)
  const getMockDashboardByYear = (year) => {
    return {
      ...DASHBOARD_DATA,
      fiscalYear: year
    };
  };

  const getMockFinancialByYear = (year) => {
    return {
      ...FINANCIAL_SUMMARY_DATA,
      fiscalYear: year
    };
  };

  // ดึงข้อมูลแดชบอร์ดจริงจาก Backend API
  const fetchDashboardData = async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const [sumRes, finRes] = await Promise.allSettled([
        axios.get(`/api/v1/trainee/dashboard/summary?fiscalYear=${selectedYear}`),
        axios.get(`/api/v1/dormitory/financial-summary?fiscalYear=${selectedYear}`)
      ]);

      if (!isMountedRef.current) return;

      if (sumRes.status === "fulfilled") {
        const payload = sumRes.value.data?.data || sumRes.value.data;
        if (payload && payload.currentlyActive && isMountedRef.current) {
          setDashboardData(formatDashboardPayload(payload, selectedYear));
        } else if (isMountedRef.current) {
          setDashboardData(getMockDashboardByYear(selectedYear));
        }
      } else if (isMountedRef.current) {
        setDashboardData(getMockDashboardByYear(selectedYear));
      }

      if (finRes.status === "fulfilled") {
        const finPayload = finRes.value.data?.data || finRes.value.data;
        if (finPayload && finPayload.totalExpectedRevenue !== undefined && isMountedRef.current) {
          setFinancialData(formatFinancialPayload(finPayload, selectedYear));
        } else if (isMountedRef.current) {
          setFinancialData(getMockFinancialByYear(selectedYear));
        }
      } else if (isMountedRef.current) {
        setFinancialData(getMockFinancialByYear(selectedYear));
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.warn("Using mock data due to API fallback mode", err);
      if (isMountedRef.current) {
        setDashboardData(getMockDashboardByYear(selectedYear));
        setFinancialData(getMockFinancialByYear(selectedYear));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isCurrent = true;
    if (!isMountedRef.current) return;
    setLoading(true);

    const loadData = async () => {
      try {
        const [sumRes, finRes, docRes, stuRes] = await Promise.allSettled([
          axios.get(`/api/v1/trainee/dashboard/summary?fiscalYear=${selectedYear}`),
          axios.get(`/api/v1/dormitory/financial-summary?fiscalYear=${selectedYear}`),
          axios.get(`/api/v1/trainee/doctors?fiscalYear=${selectedYear}`),
          axios.get(`/api/v1/trainee/students?fiscalYear=${selectedYear}`)
        ]);

        if (!isCurrent || !isMountedRef.current) return;

        if (docRes.status === "fulfilled" && docRes.value.data?.data) {
          setRawDoctors(docRes.value.data.data);
        } else {
          setRawDoctors([]);
        }

        if (stuRes.status === "fulfilled" && stuRes.value.data?.data) {
          setRawStudents(stuRes.value.data.data);
        } else {
          setRawStudents([]);
        }

        if (sumRes.status === "fulfilled") {
          const payload = sumRes.value.data?.data || sumRes.value.data;
          if (payload && payload.currentlyActive) {
            setDashboardData(formatDashboardPayload(payload, selectedYear));
          } else {
            setDashboardData(getMockDashboardByYear(selectedYear));
          }
        } else {
          setDashboardData(getMockDashboardByYear(selectedYear));
        }

        if (finRes.status === "fulfilled") {
          const finPayload = finRes.value.data?.data || finRes.value.data;
          if (finPayload && finPayload.totalExpectedRevenue !== undefined) {
            setFinancialData(formatFinancialPayload(finPayload, selectedYear));
          } else {
            setFinancialData(getMockFinancialByYear(selectedYear));
          }
        } else {
          setFinancialData(getMockFinancialByYear(selectedYear));
        }
      } catch (err) {
        if (!isCurrent || !isMountedRef.current) return;
        console.warn("Using mock data due to API fallback mode", err);
        setDashboardData(getMockDashboardByYear(selectedYear));
        setFinancialData(getMockFinancialByYear(selectedYear));
        setRawDoctors([]);
        setRawStudents([]);
      } finally {
        if (isCurrent && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCurrent = false;
    };
  }, [selectedYear]);

  // Chart Data: Bar Chart รายเดือน
  const barChartData = useMemo(() => {
    const trends = dashboardData?.monthlyTrends || {};
    return {
      labels: Array.isArray(trends.months) ? trends.months : [],
      datasets: [
        {
          label: "แพทย์ฝึกอบรม/ดูงาน (คน)",
          backgroundColor: "#0284c7",
          hoverBackgroundColor: "#0369a1",
          borderRadius: 4,
          data: Array.isArray(trends.doctors) ? trends.doctors : []
        },
        {
          label: "นักศึกษาฝึกงาน (คน)",
          backgroundColor: "#16a34a",
          hoverBackgroundColor: "#15803d",
          borderRadius: 4,
          data: Array.isArray(trends.students) ? trends.students : []
        }
      ]
    };
  }, [dashboardData?.monthlyTrends]);

  // Helper: ตรวจสอบสถานะการฝึกของผู้ฝึกงาน (ACTIVE = กำลังฝึกปัจจุบัน, PAST = สิ้นสุดการฝึกแล้ว)
  const isTraineeActive = (item) => {
    if (!item) return false;
    const now = new Date();
    let start = null;
    let end = null;
    if (item.startDate) {
      const s = new Date(item.startDate);
      if (!isNaN(s.getTime())) {
        start = s;
        start.setHours(0, 0, 0, 0);
      }
    }
    if (item.endDate) {
      const e = new Date(item.endDate);
      if (!isNaN(e.getTime())) {
        end = e;
        end.setHours(23, 59, 59, 999);
      }
    }
    if (start && end) return now >= start && now <= end;
    if (start && !end) return now >= start;
    const st = (item.statusText || item.status || "").trim();
    if (st.includes("สำเร็จ") || st.includes("เสร็จ") || st.includes("ออกไปแล้ว") || st.includes("พ้น")) return false;
    if (st.includes("รอ") || st.includes("ยังไม่")) return false;
    if (st.includes("กำลังฝึก") || st.includes("ปฏิบัติงาน")) return true;
    return false;
  };

  const isTraineePast = (item) => {
    if (!item) return false;
    const now = new Date();
    let end = null;
    if (item.endDate) {
      const e = new Date(item.endDate);
      if (!isNaN(e.getTime())) {
        end = e;
        end.setHours(23, 59, 59, 999);
      }
    }
    if (end && now > end) return true;
    const st = (item.statusText || item.status || "").trim();
    if (st.includes("สำเร็จ") || st.includes("เสร็จ") || st.includes("ออกไปแล้ว") || st.includes("พ้น")) return true;
    return false;
  };

  const filteredChartDoctors = useMemo(() => {
    if (chartStatusFilter === "ACTIVE") return rawDoctors.filter(isTraineeActive);
    if (chartStatusFilter === "PAST") return rawDoctors.filter(isTraineePast);
    return rawDoctors;
  }, [rawDoctors, chartStatusFilter]);

  const filteredChartStudents = useMemo(() => {
    if (chartStatusFilter === "ACTIVE") return rawStudents.filter(isTraineeActive);
    if (chartStatusFilter === "PAST") return rawStudents.filter(isTraineePast);
    return rawStudents;
  }, [rawStudents, chartStatusFilter]);

  // Chart Data: Doughnut Chart กลุ่มงาน รพ.
  const doughnutData = useMemo(() => {
    if (chartStatusFilter === "ACTIVE") {
      const deptMap = {};
      filteredChartDoctors.forEach(d => {
        const name = d.departmentName || "กลุ่มงานพยาบาลผู้ป่วยนอก";
        deptMap[name] = (deptMap[name] || 0) + (d.batchCount || 1);
      });
      filteredChartStudents.forEach(s => {
        const name = s.departmentName || "กลุ่มงานเภสัชกรรม";
        deptMap[name] = (deptMap[name] || 0) + (s.batchCount || 1);
      });

      const colors = ["#0284c7", "#16a34a", "#0ea5e9", "#d97706", "#7c3aed", "#ec4899"];
      const entries = Object.entries(deptMap);
      if (entries.length > 0) {
        return {
          labels: entries.map(x => x[0]),
          datasets: [
            {
              data: entries.map(x => x[1]),
              backgroundColor: entries.map((_, i) => colors[i % colors.length]),
              borderWidth: 2,
              borderColor: "#ffffff"
            }
          ],
          total: entries.reduce((s, x) => s + x[1], 0)
        };
      }
    }

    const deptList = Array.isArray(dashboardData?.departmentBreakdown) ? dashboardData.departmentBreakdown : [];
    return {
      labels: deptList.map(d => d.name || ""),
      datasets: [
        {
          data: deptList.map(d => d.total || 0),
          backgroundColor: deptList.map(d => d.color || "#0284c7"),
          hoverBackgroundColor: deptList.map(d => d.color || "#0369a1"),
          borderWidth: 2,
          borderColor: "#ffffff"
        }
      ],
      total: deptList.reduce((s, d) => s + (d.total || 0), 0)
    };
  }, [chartStatusFilter, filteredChartDoctors, filteredChartStudents, dashboardData?.departmentBreakdown]);

  // Chart Data: Top Sending Institutions (Horizontal Bar)
  const institutionChartData = useMemo(() => {
    const instMap = {};
    const docs = filteredChartDoctors;
    const stus = filteredChartStudents;

    docs.forEach(d => {
      let name = (d.institutionName || d.institution || "ไม่ระบุ").trim();
      if (name.includes("จุฬาภรณ์")) name = "ร.ร.จุฬาภรณราชวิทยาลัย ตรัง";
      else if (name.includes("ควนวิเศษ")) name = "ร.ร.มัธยมวัดควนวิเศษ";
      else if (name.includes("สงขลานครินทร์") || name.includes("มอ.")) name = "ม.สงขลานครินทร์ (ม.อ.)";
      else if (name.includes("สถาบันโรคผิวหนัง")) name = "สถาบันโรคผิวหนัง";
      else if (name.includes("บุรีรัมย์")) name = "รพ.บุรีรัมย์";
      instMap[name] = (instMap[name] || 0) + (d.batchCount || 1);
    });

    stus.forEach(s => {
      let name = (s.institutionName || s.institution || "ไม่ระบุ").trim();
      if (name.includes("จุฬาภรณ์")) name = "ร.ร.จุฬาภรณราชวิทยาลัย ตรัง";
      else if (name.includes("ควนวิเศษ")) name = "ร.ร.มัธยมวัดควนวิเศษ";
      else if (name.includes("สงขลานครินทร์") || name.includes("มอ.")) name = "ม.สงขลานครินทร์ (ม.อ.)";
      else if (name.includes("สถาบันโรคผิวหนัง")) name = "สถาบันโรคผิวหนัง";
      else if (name.includes("บุรีรัมย์")) name = "รพ.บุรีรัมย์";
      instMap[name] = (instMap[name] || 0) + (s.batchCount || 1);
    });

    let sorted = Object.entries(instMap).sort((a, b) => b[1] - a[1]).slice(0, 7);
    if (sorted.length === 0) {
      if (chartStatusFilter === "ACTIVE") {
        sorted = [
          ["ม.สงขลานครินทร์ (ม.อ.)", 2],
          ["รพ.บุรีรัมย์", 2],
          ["สถาบันโรคผิวหนัง", 1],
          ["โรงเรียนสีมาบริรักษ์", 1]
        ];
      } else {
        sorted = [
          ["ม.สงขลานครินทร์ (ม.อ.)", 30],
          ["ร.ร.มัธยมวัดควนวิเศษ", 11],
          ["ร.ร.จุฬาภรณราชวิทยาลัย ตรัง", 11],
          ["โรงเรียนสภาราชินี ตรัง", 9],
          ["สถาบันโรคผิวหนัง", 9],
          ["วิทยาลัยเทคนิคตรัง", 7],
          ["รพ.บุรีรัมย์", 3]
        ];
      }
    }

    return {
      labels: sorted.map(x => x[0]),
      datasets: [
        {
          label: "จำนวนผู้ฝึกอบรม (คน)",
          data: sorted.map(x => x[1]),
          backgroundColor: "#6366f1",
          hoverBackgroundColor: "#4f46e5",
          borderRadius: 4
        }
      ]
    };
  }, [filteredChartDoctors, filteredChartStudents, chartStatusFilter]);

  // Chart Data: Dormitory Occupancy Breakdown by Building (Doughnut)
  const dormBuildingChartData = useMemo(() => {
    let b1 = 0, b2 = 0, b3 = 0, b4 = 0, noDorm = 0;
    const docs = filteredChartDoctors;
    const stus = filteredChartStudents;

    docs.forEach(d => {
      if (!d.requiresAccommodation) { noDorm++; return; }
      const rm = d.assignedRoomNumber || "";
      if (rm.startsWith("2")) b2++;
      else if (rm.startsWith("3")) b3++;
      else if (rm.startsWith("4")) b4++;
      else b2++;
    });

    stus.forEach(s => {
      const cnt = s.batchCount || 1;
      if (!s.requiresAccommodation) { noDorm += cnt; return; }
      const rm = s.assignedRoomNumber || "";
      if (rm.startsWith("2")) b2 += cnt;
      else if (rm.startsWith("3")) b3 += cnt;
      else if (rm.startsWith("4")) b4 += cnt;
      else b1 += cnt;
    });

    if (b1 === 0 && b2 === 0 && b3 === 0 && b4 === 0 && noDorm === 0) {
      if (chartStatusFilter === "ACTIVE") {
        b1 = 1; b2 = 2; b3 = 0; b4 = 0; noDorm = 3;
      } else {
        b1 = 11; b2 = 8; b3 = 0; b4 = 1; noDorm = 93;
      }
    }

    const totalWithDorm = b1 + b2 + b3 + b4;
    const totalAll = totalWithDorm + noDorm;
    const dormRate = totalAll > 0 ? Math.round((totalWithDorm / totalAll) * 100) : 0;

    return {
      labels: [
        `อาคาร 1: ${b1} คน`,
        `อาคาร 2: ${b2} คน`,
        `อาคาร 3: ${b3} คน`,
        `อาคาร 4: ${b4} คน`,
        `ไม่ได้พัก: ${noDorm} คน`
      ],
      datasets: [
        {
          data: [b1, b2, b3, b4, noDorm],
          backgroundColor: ["#0ea5e9", "#0284c7", "#8b5cf6", "#d97706", "#94a3b8"],
          borderWidth: 2,
          borderColor: "#ffffff"
        }
      ],
      totalWithDorm,
      totalNoDorm: noDorm,
      totalAll,
      dormRate
    };
  }, [filteredChartDoctors, filteredChartStudents, chartStatusFilter]);

  // Chart Data: Program & Role Distribution (Doughnut)
  const programRoleChartData = useMemo(() => {
    let fellow = filteredChartDoctors.filter(d => (d.doctorPositionType || "").includes("fellow")).length;
    let elective = filteredChartDoctors.filter(d => !(d.doctorPositionType || "").includes("fellow")).length;
    let rx = filteredChartStudents.filter(s => (s.majorField || "").includes("เภสัช") || (s.traineeRole || "").includes("เภสัช") || (s.departmentName || "").includes("เภสัช")).length;
    let nursing = filteredChartStudents.filter(s => (s.majorField || "").includes("พยาบาล") || (s.traineeRole || "").includes("พยาบาล") || (s.departmentName || "").includes("พยาบาล")).length;
    let lab = filteredChartStudents.filter(s => (s.majorField || "").includes("พยาธิ") || (s.departmentName || "").includes("พยาธิ") || (s.traineeRole || "").includes("แลป")).length;
    let school = filteredChartStudents.filter(s => (s.positionLevel || "").includes("ม.") || (s.positionLevel || "").includes("นักเรียน")).length;

    if (fellow === 0 && elective === 0 && rx === 0 && nursing === 0 && lab === 0 && school === 0) {
      if (chartStatusFilter === "ACTIVE") {
        fellow = 1; elective = 2; rx = 2; nursing = 1; lab = 0; school = 0;
      } else {
        fellow = 9; elective = 10; rx = 35; lab = 20; school = 5; nursing = 1;
      }
    }

    const items = [
      { label: `นศ.เภสัชศาสตร์ (Rx): ${rx} คน`, count: rx, color: "#16a34a" },
      { label: `แพทย์ Elective: ${elective} คน`, count: elective, color: "#0284c7" },
      { label: `แพทย์ Fellow ตจวิทยา: ${fellow} คน`, count: fellow, color: "#2563eb" },
      { label: `นศ.เทคนิคการแพทย์/แลป: ${lab} คน`, count: lab, color: "#06b6d4" },
      { label: `นศ.พยาบาล/ผู้ช่วย: ${nursing} คน`, count: nursing, color: "#ec4899" },
      { label: `นักเรียนมัธยมโครงการวิทย์: ${school} คน`, count: school, color: "#f59e0b" }
    ].filter(x => x.count > 0);

    const totalCount = items.reduce((sum, x) => sum + x.count, 0);

    return {
      labels: items.map(x => x.label),
      datasets: [
        {
          data: items.map(x => x.count),
          backgroundColor: items.map(x => x.color),
          borderWidth: 2,
          borderColor: "#ffffff"
        }
      ],
      totalCount
    };
  }, [filteredChartDoctors, filteredChartStudents, chartStatusFilter]);

  // นับจำนวนผู้เข้าพักตามสถานะห้องพัก (ปัจจุบัน vs ประวัติ vs ทั้งหมด)
  const dormCounts = useMemo(() => {
    const buildingsList = (dashboardData?.dormitory && dashboardData.dormitory.buildings && dashboardData.dormitory.buildings.length > 0)
      ? dashboardData.dormitory.buildings
      : DORM_OCCUPIED_BY_BUILDINGS;

    let active = 0;
    let past = 0;
    let total = 0;

    buildingsList.forEach(b => {
      (b.floors || []).forEach(fl => {
        (fl.rooms || []).forEach(rm => {
          total += 1;
          const isActive = isStayActive(rm);
          if (isActive) {
            active += 1;
          } else {
            past += 1;
          }
        });
      });
    });

    return { active, past, total };
  }, [dashboardData?.dormitory]);

  // กรองตึกหอพักตาม Tab ตึก และ สถานะการพัก (ปัจจุบัน vs ประวัติ)
  const filteredBuildings = useMemo(() => {
    const buildingsList = (dashboardData?.dormitory && dashboardData.dormitory.buildings && dashboardData.dormitory.buildings.length > 0)
      ? dashboardData.dormitory.buildings
      : DORM_OCCUPIED_BY_BUILDINGS;

    const byBuilding = selectedBuildingTab === "ALL"
      ? buildingsList
      : buildingsList.filter(b => b.buildingNo === selectedBuildingTab);

    return byBuilding.map(b => {
      const filteredFloors = (b.floors || []).map(fl => {
        const filteredRooms = (fl.rooms || []).filter(rm => {
          if (dormStatusFilter === "ALL") return true;
          const isActive = isStayActive(rm);
          if (dormStatusFilter === "ACTIVE") return isActive;
          if (dormStatusFilter === "PAST") return !isActive;
          return true;
        });
        return {
          ...fl,
          rooms: filteredRooms
        };
      }).filter(fl => fl.rooms.length > 0);

      const residentsCount = filteredFloors.reduce((sum, fl) => sum + fl.rooms.length, 0);

      return {
        ...b,
        residentsCount,
        floors: filteredFloors
      };
    });
  }, [dashboardData?.dormitory, selectedBuildingTab, dormStatusFilter]);

  return (
    <div className={classes.container}>
      {/* 1. Header Banner & Year Selector */}
      <div className={classes.headerBanner}>
        <div className="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <div className="d-flex align-items-center mb-1">
              <span className={classes.liveBadge}>
                <span className={classes.liveDot} />
                ระบบบริหารจัดการแพทย์และนักศึกษาฝึกงาน
              </span>
              <span style={{ fontSize: "0.85rem", color: "#f8fafc", fontWeight: "600" }}>
                รพ.โรคผิวหนังเขตร้อนภาคใต้ จ.ตรัง
              </span>
              {canManage ? (
                <span style={{ backgroundColor: "rgba(14, 165, 233, 0.25)", border: "1px solid #38bdf8", color: "#ffffff", padding: "3px 10px", borderRadius: "12px", fontSize: "0.76rem", fontWeight: "700", marginLeft: "10px" }}>
                  ⚡ สิทธิ์จัดการข้อมูล (งานคอมฯ / วิจัยถ่ายทอด)
                </span>
              ) : (
                <span style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", border: "1px solid rgba(255, 255, 255, 0.3)", color: "#ffffff", padding: "3px 10px", borderRadius: "12px", fontSize: "0.76rem", fontWeight: "600", marginLeft: "10px" }}>
                  👁️ สิทธิ์เข้าชม (ดู Dashboard)
                </span>
              )}
            </div>
            <h3 style={{ margin: "6px 0 0", fontWeight: "800", color: "#ffffff", letterSpacing: "-0.5px" }}>
              <DashboardIcon style={{ verticalAlign: "middle", marginRight: "10px", fontSize: "2rem", color: "#38bdf8" }} />
              แดชบอร์ดภาพรวมการฝึกปฏิบัติงาน (Trainee Operations)
            </h3>
            <p style={{ margin: "6px 0 0", color: "#f1f5f9", fontSize: "0.92rem", fontWeight: "500" }}>
              สรุปจำนวนผู้ฝึกปัจจุบัน • กำหนดการรอบถัดไป • สังกัดกลุ่มงาน รพ. • ผังห้องพัก 4 อาคารสำหรับผู้ที่ขอห้องพักไว้
            </p>
          </div>

          {/* Year Filter Pills */}
          <div className="d-flex align-items-center mt-3 mt-lg-0 flex-wrap">
            <span style={{ fontSize: "0.9rem", marginRight: "12px", color: "#ffffff", fontWeight: "700" }}>
              ปีงบประมาณไทย (1 ต.ค. - 30 ก.ย.):
            </span>
            {fiscalYearOptions.map(yr => (
              <span
                key={yr}
                onClick={() => setSelectedYear(yr)}
                className={`${classes.yearPill} ${selectedYear === yr ? classes.yearPillActive : ""}`}
              >
                ปี {yr}
              </span>
            ))}
            <button
              onClick={() => fetchDashboardData()}
              disabled={loading}
              title="รีเฟรชข้อมูล"
              style={{
                background: "rgba(255,255,255,0.25)",
                border: "1px solid #ffffff",
                borderRadius: "50%",
                width: "38px",
                height: "38px",
                color: "#ffffff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "4px",
                transition: "all 0.2s"
              }}
            >
              <SyncIcon style={{ fontSize: "1.3rem" }} />
            </button>
          </div>
        </div>
      </div>

      {/* Mode Switcher Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "10px",
          backgroundColor: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          padding: "10px 14px",
          marginBottom: "20px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          <button
            type="button"
            className={`btn ${dashboardMode === "OPERATIONAL" ? "btn-primary font-weight-bold" : "btn-outline-secondary"}`}
            style={{
              borderRadius: "10px",
              padding: "8px 18px",
              fontSize: "0.92rem",
              backgroundColor: dashboardMode === "OPERATIONAL" ? "#0284c7" : "transparent",
              borderColor: dashboardMode === "OPERATIONAL" ? "#0284c7" : "#cbd5e1",
              color: dashboardMode === "OPERATIONAL" ? "#ffffff" : "#334155"
            }}
            onClick={() => setDashboardMode("OPERATIONAL")}
          >
            <PeopleIcon style={{ verticalAlign: "middle", marginRight: "6px", fontSize: "1.2rem" }} />
            {SHOW_FINANCIAL_TAB ? "1. " : ""}ภาพรวมแพทย์และนักศึกษาฝึกงาน (กำลังฝึก / จะมา / กลุ่มงาน / ผังห้องพัก)
          </button>

          {/* ซ่อนแท็บ 2. Dashboard การเงินค่าหอพัก (ยอดเก็บ) ไปก่อนตามคำขอผู้ใช้ */}
          {SHOW_FINANCIAL_TAB && (
            <button
              type="button"
              className={`btn ${dashboardMode === "FINANCIAL" ? "btn-success font-weight-bold" : "btn-outline-secondary"}`}
              style={{
                borderRadius: "10px",
                padding: "8px 18px",
                fontSize: "0.92rem",
                backgroundColor: dashboardMode === "FINANCIAL" ? "#047857" : "transparent",
                borderColor: dashboardMode === "FINANCIAL" ? "#047857" : "#cbd5e1",
                color: dashboardMode === "FINANCIAL" ? "#ffffff" : "#334155"
              }}
              onClick={() => setDashboardMode("FINANCIAL")}
            >
              <AttachMoneyIcon style={{ verticalAlign: "middle", marginRight: "6px", fontSize: "1.2rem" }} />
              2. Dashboard การเงินค่าหอพัก (ยอดเก็บได้ / ค้างชำระ / ทวงถาม / คำนวณ Proration)
            </button>
          )}
        </div>

        {SHOW_FINANCIAL_TAB && dashboardMode === "FINANCIAL" && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ backgroundColor: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", padding: "4px 12px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "700" }}>
              💰 อัตราการจัดเก็บ: {financialData.collectionRate}%
            </span>
          </div>
        )}
      </div>

      {/* Overall Status Breakdown Banner (สรุปสถานะผู้ฝึกงานทั้งระบบ: กำลังฝึก, รอเริ่มฝึก, สำเร็จการฝึก, ยกเลิก) */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "20px", padding: "12px 18px", backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #cbd5e1", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
          <span style={{ fontSize: "0.88rem", fontWeight: "800", color: "#0f172a" }}>
            📊 สถานะผู้ฝึกงานทั้งหมด (ปีงบ {selectedYear}):
          </span>
          <span style={{ backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "700" }}>
            🟢 กำลังฝึก: <b>{statsSummary.activeTotal}</b> คน (แพทย์ {statsSummary.activeDocs}, นศ. {statsSummary.activeStus})
          </span>
          <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "700" }}>
            🔵 รอเริ่มฝึก: <b>{statsSummary.upcomingTotal}</b> คน (แพทย์ {statsSummary.upcomingDocs})
          </span>
          <span style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "700" }}>
            ⚪ สำเร็จการฝึกแล้ว: <b>{statsSummary.completedTotal}</b> คน (แพทย์ {statsSummary.completedDocs}, นศ. {statsSummary.completedStus})
          </span>
          <span style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "800" }}>
            🔴 ยกเลิก / ไม่มา: <b>{statsSummary.cancelledTotal}</b> คน (นศ. {statsSummary.cancelledStus} คน)
          </span>
        </div>

        <div style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: "600" }}>
          🕒 ข้อมูลประจำปีงบประมาณ {selectedYear}
        </div>
      </div>

      {dashboardMode === "OPERATIONAL" && (
        <div>
          {/* 2. Top 4 High-Contrast KPI Cards (สีเข้ม คมจัด มองเห็นง่าย สบายตา) */}
          <GridContainer>
        {/* Card 1: แพทย์ฝึกอบรม / ดูงาน */}
        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <div classes={classes.iconBoxTeal}>
                    <LocalHospitalIcon style={{ fontSize: "24px" }} />
                  </div>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                      แพทย์ฝึกอบรม / ดูงาน
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "600" }}>
                      Fellowship & Elective
                    </div>
                  </div>
                </div>
                <Badge color="info" pill style={{ fontSize: "0.76rem", backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: "700", border: "1px solid #7dd3fc" }}>
                  กำลังฝึก {statsSummary.activeDocs} คน
                </Badge>
              </div>

              {/* Big Metric */}
              <div className="d-flex align-items-baseline my-2">
                <span style={{ fontSize: "2.5rem", fontWeight: "900", color: "#0369a1", lineHeight: "1" }}>
                  {statsSummary.docTotal}
                </span>
                <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", marginLeft: "8px" }}>
                  คน
                </span>
                <span style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600", marginLeft: "8px" }}>
                  (รวมปีงบ {selectedYear})
                </span>
              </div>

              {/* Sub-tags */}
              <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                <span className={classes.miniPillDark}>
                  Fellow: <b style={{ color: "#0369a1", marginLeft: "4px" }}>{statsSummary.docFellow}</b>
                </span>
                <span className={classes.miniPillDark}>
                  Elective: <b style={{ color: "#0369a1", marginLeft: "4px" }}>{statsSummary.docElective}</b>
                </span>
              </div>
            </div>

            <div>
              <div className={classes.cardDivider} />
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.82rem", color: "#15803d", fontWeight: "700" }}>
                <span>
                  <CheckCircleIcon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }} />
                  สำเร็จแล้ว {statsSummary.completedDocs} คน • รอฝึก {statsSummary.upcomingDocs} คน
                </span>
              </div>
            </div>
          </div>
        </GridItem>

        {/* Card 2: นักศึกษาฝึกงาน */}
        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <div className={classes.iconBoxGreen}>
                    <SchoolIcon style={{ fontSize: "24px" }} />
                  </div>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                      นักศึกษาฝึกงาน
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "600" }}>
                      สหสาขาวิชาชีพ ป.ตรี
                    </div>
                  </div>
                </div>
                <Badge color="success" pill style={{ fontSize: "0.76rem", backgroundColor: "#dcfce7", color: "#15803d", fontWeight: "700", border: "1px solid #86efac" }}>
                  กำลังฝึก {statsSummary.activeStus} คน
                </Badge>
              </div>

              {/* Big Metric */}
              <div className="d-flex align-items-baseline my-2">
                <span style={{ fontSize: "2.5rem", fontWeight: "900", color: "#15803d", lineHeight: "1" }}>
                  {statsSummary.stuTotal}
                </span>
                <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", marginLeft: "8px" }}>
                  คน
                </span>
                <span style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600", marginLeft: "8px" }}>
                  (รวมปีงบ {selectedYear})
                </span>
              </div>

              {/* Sub-tags */}
              <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                <span className={classes.miniPillDark}>เภสัช: <b style={{ color: "#15803d", marginLeft: "2px" }}>{statsSummary.stuPharmacy}</b></span>
                <span className={classes.miniPillDark}>พยาบาล: <b style={{ color: "#15803d", marginLeft: "2px" }}>{statsSummary.stuNursing}</b></span>
                <span className={classes.miniPillDark}>แลป: <b style={{ color: "#15803d", marginLeft: "2px" }}>{statsSummary.stuLab}</b></span>
                <span className={classes.miniPillDark} style={{ backgroundColor: "#fee2e2", borderColor: "#fca5a5", color: "#991b1b" }}>
                  ยกเลิก: <b style={{ color: "#dc2626", marginLeft: "2px" }}>{statsSummary.cancelledStus}</b> คน
                </span>
              </div>
            </div>

            <div>
              <div className={classes.cardDivider} />
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.82rem", color: "#0369a1", fontWeight: "700" }}>
                <span>
                  <PeopleIcon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }} />
                  สำเร็จการฝึกแล้ว {statsSummary.completedStus} คน
                </span>
                <span style={{ color: "#334155", fontSize: "0.78rem" }}>ฝึกตามแผนก</span>
              </div>
            </div>
          </div>
        </GridItem>

        {/* Card 3: รวมผู้ฝึกงานทั้งหมด & การพักหอพัก */}
        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <div className={classes.iconBoxPurple}>
                    <PeopleIcon style={{ fontSize: "24px" }} />
                  </div>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                      รวมผู้ฝึกงานทั้งหมด
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "600" }}>
                      แพทย์ & นศ.ฝึกงาน
                    </div>
                  </div>
                </div>
                <Badge color="primary" pill style={{ fontSize: "0.76rem", backgroundColor: "#f3e8ff", color: "#6b21a8", fontWeight: "700", border: "1px solid #c084fc" }}>
                  พักหอ {financialData.totalResidentsCount ?? statsSummary.withDorm} คน
                </Badge>
              </div>

              {/* Big Metric */}
              <div className="d-flex align-items-baseline my-2">
                <span style={{ fontSize: "2.5rem", fontWeight: "900", color: "#581c87", lineHeight: "1" }}>
                  {statsSummary.totalTrainees}
                </span>
                <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", marginLeft: "8px" }}>
                  คน
                </span>
                <span style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600", marginLeft: "8px" }}>
                  (รวมปีงบ {selectedYear})
                </span>
              </div>

              {/* Sub-tags */}
              <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                <span className={classes.miniPillDark} style={{ backgroundColor: "#faf5ff", borderColor: "#c084fc" }}>
                  🏢 พักหอ รพ.: <b style={{ color: "#581c87", marginLeft: "4px" }}>{financialData.totalResidentsCount ?? statsSummary.withDorm}</b> คน
                </span>
                <span className={classes.miniPillDark}>
                  🚶 ไม่พักหอ: <b style={{ color: "#334155", marginLeft: "4px" }}>{Math.max(0, statsSummary.totalTrainees - (financialData.totalResidentsCount ?? statsSummary.withDorm))}</b> คน
                </span>
                <span className={classes.miniPillDark} style={{ backgroundColor: "#fee2e2", borderColor: "#fca5a5", color: "#991b1b" }}>
                  ❌ ยกเลิก: <b style={{ color: "#dc2626", marginLeft: "4px" }}>{statsSummary.cancelledTotal}</b> คน
                </span>
              </div>
            </div>

            <div>
              <div className={classes.cardDivider} />
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.82rem", color: "#6b21a8", fontWeight: "700" }}>
                <span>
                  <HotelIcon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }} />
                  สำเร็จ {statsSummary.completedTotal} คน • กำลังฝึก {statsSummary.activeTotal} คน
                </span>
                <span style={{ color: "#334155", fontSize: "0.78rem" }}>ตึก 1, 2, 3, 4</span>
              </div>
            </div>
          </div>
        </GridItem>

        {/* Card 4: ผู้ที่จะมาในรอบถัดไป (Upcoming Inflow) */}
        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <div className={classes.iconBoxOrange}>
                    <EventIcon style={{ fontSize: "24px" }} />
                  </div>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                      กลุ่มที่จะมาถัดไป
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "600" }}>
                      Upcoming Inflow (ปีงบ {selectedYear})
                    </div>
                  </div>
                </div>
                <Badge color="warning" pill style={{ fontSize: "0.76rem", backgroundColor: "#ffedd5", color: "#c2410c", fontWeight: "700", border: "1px solid #fb923c" }}>
                  {statsSummary.upcomingTotal > 0 ? `รอเริ่ม ${statsSummary.upcomingTotal} คน` : "ไม่มีรอบรอเริ่ม"}
                </Badge>
              </div>

              {/* Big Metric */}
              <div className="d-flex align-items-baseline my-2">
                <span style={{ fontSize: "2.5rem", fontWeight: "900", color: "#9a3412", lineHeight: "1" }}>
                  +{statsSummary.upcomingTotal}
                </span>
                <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", marginLeft: "8px" }}>
                  คน
                </span>
                <span style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600", marginLeft: "8px" }}>
                  (รอเริ่มฝึก)
                </span>
              </div>

              {/* Sub-tags */}
              <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                <span className={classes.miniPillDark} style={{ backgroundColor: "#fff7ed", borderColor: "#fdba74" }}>
                  แพทย์: <b style={{ color: "#9a3412", marginLeft: "4px" }}>{statsSummary.upcomingDocs}</b> คน
                </span>
                <span className={classes.miniPillDark}>
                  นศ.: <b style={{ color: "#0369a1", marginLeft: "4px" }}>{statsSummary.upcomingStus}</b> คน
                </span>
              </div>
            </div>

            <div>
              <div className={classes.cardDivider} />
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "0.82rem", color: "#c2410c", fontWeight: "700" }}>
                <span>
                  <QueryBuilderIcon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }} />
                  {statsSummary.upcomingTotal > 0 ? `มีกำหนดการ ${dashboardData.upcomingIntake?.length || 0} กลุ่ม` : "ไม่มีกำหนดการคงค้าง"}
                </span>
                <span style={{ color: "#334155", fontSize: "0.78rem" }}>ปี {selectedYear}</span>
              </div>
            </div>
          </div>
        </GridItem>
      </GridContainer>

      {/* ========================================================================= */}
      {/* 🟢 NEW SHOWCASE: ใครกำลังฝึกปฏิบัติงานอยู่ในขณะนี้ (Currently Active Trainees On-Duty) */}
      {/* ========================================================================= */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "18px",
          border: "1px solid #cbd5e1",
          boxShadow: "0 6px 20px -2px rgba(15, 23, 42, 0.08)",
          marginBottom: "26px",
          overflow: "hidden"
        }}
      >
        {/* Top Header of Showcase */}
        <div
          style={{
            padding: "20px 24px",
            background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
            borderBottom: "1px solid #e2e8f0"
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ gap: "12px" }}>
            <div>
              <div className="d-flex align-items-center mb-1">
                <span className={classes.activeLiveDot} />
                <span
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: "800",
                    color: "#166534",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  LIVE ON-DUTY ROSTER • ประจำการจริงในโรงพยาบาล
                </span>
                <span
                  style={{
                    backgroundColor: "#ecfdf5",
                    color: "#15803d",
                    border: "1px solid #86efac",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "0.76rem",
                    fontWeight: "800",
                    marginLeft: "8px"
                  }}
                >
                  {filteredActiveTrainees.length} ท่าน
                </span>
              </div>
              <h4
                style={{
                  margin: "4px 0 0",
                  fontWeight: "900",
                  color: "#0f172a",
                  fontSize: "1.35rem",
                  letterSpacing: "-0.5px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                ผู้ฝึกปฏิบัติงานที่กำลังประจำการอยู่ในขณะนี้ (Active Trainees)
              </h4>
              <p style={{ margin: "4px 0 0", color: "#475569", fontSize: "0.88rem", fontWeight: "500" }}>
                ตรวจสอบรายชื่อแพทย์และนักศึกษาที่อยู่ระหว่างการฝึก • สังกัดกลุ่มงาน • ความคืบหน้าการฝึก • และหอพัก รพ.
              </p>
            </div>

            {/* View Mode Switcher Pills (Card Grid vs Table View) */}
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <div
                style={{
                  display: "inline-flex",
                  backgroundColor: "#f1f5f9",
                  padding: "4px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1"
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveRosterViewMode("GRID")}
                  style={{
                    border: "none",
                    backgroundColor: activeRosterViewMode === "GRID" ? "#0284c7" : "transparent",
                    color: activeRosterViewMode === "GRID" ? "#ffffff" : "#475569",
                    fontWeight: "700",
                    fontSize: "0.84rem",
                    padding: "6px 14px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    boxShadow: activeRosterViewMode === "GRID" ? "0 2px 6px rgba(2, 132, 199, 0.3)" : "none",
                    transition: "all 0.2s"
                  }}
                >
                  <ViewModuleIcon style={{ fontSize: "1.1rem" }} />
                  การ์ดโปรไฟล์โมเดิร์น
                </button>
                <button
                  type="button"
                  onClick={() => setActiveRosterViewMode("TABLE")}
                  style={{
                    border: "none",
                    backgroundColor: activeRosterViewMode === "TABLE" ? "#0284c7" : "transparent",
                    color: activeRosterViewMode === "TABLE" ? "#ffffff" : "#475569",
                    fontWeight: "700",
                    fontSize: "0.84rem",
                    padding: "6px 14px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    boxShadow: activeRosterViewMode === "TABLE" ? "0 2px 6px rgba(2, 132, 199, 0.3)" : "none",
                    transition: "all 0.2s"
                  }}
                >
                  <ViewListIcon style={{ fontSize: "1.1rem" }} />
                  ตารางข้อมูล
                </button>
              </div>
            </div>
          </div>

          {/* Sub Toolbar: Quick Search & Filter Chips */}
          <div
            className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3"
            style={{ borderTop: "1px dashed #cbd5e1", gap: "10px" }}
          >
            {/* Filter Chips */}
            <div className="d-flex flex-wrap align-items-center" style={{ gap: "6px" }}>
              <span style={{ fontSize: "0.82rem", fontWeight: "800", color: "#334155", marginRight: "4px" }}>
                กรองประเภท:
              </span>
              {[
                { key: "ALL", label: `ทั้งหมด (${activeRosterCounts.all})` },
                { key: "DOCTOR", label: `👨‍⚕️ แพทย์ (${activeRosterCounts.doctors})` },
                { key: "STUDENT", label: `🎓 นักศึกษา (${activeRosterCounts.students})` },
                { key: "DORM", label: `🏢 พักหอ รพ. (${activeRosterCounts.dorm})` },
                { key: "NONDORM", label: `🚶 ไป-กลับ (${activeRosterCounts.nonDorm})` }
              ].map(chip => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setActiveRosterTypeFilter(chip.key)}
                  style={{
                    border: activeRosterTypeFilter === chip.key ? "1px solid #0284c7" : "1px solid #cbd5e1",
                    backgroundColor: activeRosterTypeFilter === chip.key ? "#e0f2fe" : "#ffffff",
                    color: activeRosterTypeFilter === chip.key ? "#0369a1" : "#475569",
                    fontWeight: activeRosterTypeFilter === chip.key ? "800" : "600",
                    fontSize: "0.8rem",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* Quick Search Box */}
            <div style={{ position: "relative", minWidth: "240px", maxWidth: "340px", flex: "1 1 240px" }}>
              <SearchIcon
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: "1.1rem"
                }}
              />
              <input
                type="text"
                className="form-control"
                placeholder="ค้นหาชื่อ, กลุ่มงาน, หรือสถาบัน..."
                value={activeRosterSearch}
                onChange={(e) => setActiveRosterSearch(e.target.value)}
                style={{
                  paddingLeft: "34px",
                  fontSize: "0.85rem",
                  borderRadius: "10px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#ffffff",
                  height: "36px"
                }}
              />
              {activeRosterSearch && (
                <button
                  type="button"
                  onClick={() => setActiveRosterSearch("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "bold"
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Body: Card Grid or Table */}
        <div style={{ padding: "20px 24px" }}>
          {activeRosterViewMode === "GRID" ? (
            <div>
              {filteredActiveTrainees.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
                  <PeopleIcon style={{ fontSize: "3rem", color: "#cbd5e1", marginBottom: "8px" }} />
                  <div style={{ fontWeight: "800", fontSize: "1.05rem", color: "#334155" }}>
                    {selectedYear < currentFiscalYear
                      ? `สิ้นสุดรอบปีงบประมาณ ${selectedYear} แล้ว`
                      : selectedYear > currentFiscalYear
                      ? `ยังไม่ถึงรอบปีงบประมาณ ${selectedYear}`
                      : "ไม่พบรายชื่อผู้ฝึกปฏิบัติงานที่ตรงกับเงื่อนไข"}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "4px" }}>
                    {selectedYear < currentFiscalYear
                      ? "ผู้ฝึกปฏิบัติงานในรอบปีนี้สำเร็จการฝึกครบถ้วนทั้งหมดแล้ว"
                      : selectedYear > currentFiscalYear
                      ? "ยังไม่มีผู้เข้าฝึกประจำการสำหรับรอบปีงบประมาณนี้"
                      : "ลองปรับเปลี่ยนคำค้นหา หรือเลือกตัวกรองประเภทอื่น"}
                  </div>
                </div>
              ) : (
                <div className="row" style={{ rowGap: "20px" }}>
                  {filteredActiveTrainees.map((trainee, idx) => {
                    const theme = getDepartmentTheme(trainee.department, trainee.type);
                    const initials = getAvatarInitials(trainee.name);
                    const progress = calculateRotationProgress(trainee.dateRange);
                    const isDoc = (trainee.type || "").includes("แพทย์");

                    return (
                      <div key={trainee.id || idx} className="col-12 col-md-6 col-lg-4">
                        <div className={classes.traineeCard}>
                          {/* Top Accent Gradient Bar */}
                          <div style={{ height: "4px", background: theme.gradient, width: "100%" }} />

                          <div style={{ padding: "18px 20px 14px" }}>
                            {/* Header Row: Avatar + Name + Live Badge */}
                            <div className="d-flex align-items-start justify-content-between mb-3" style={{ gap: "10px" }}>
                              <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0, flex: 1 }}>
                                {/* Avatar with Initials + Live Pulse Dot */}
                                <div style={{ position: "relative", flexShrink: 0 }}>
                                  <div
                                    style={{
                                      width: "48px",
                                      height: "48px",
                                      borderRadius: "50%",
                                      background: theme.gradient,
                                      color: "#ffffff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "800",
                                      fontSize: "1.05rem",
                                      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                                      letterSpacing: "-0.5px"
                                    }}
                                  >
                                    {initials}
                                  </div>
                                  {/* Live Online Badge on Avatar */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-1px",
                                      right: "-1px",
                                      width: "14px",
                                      height: "14px",
                                      borderRadius: "50%",
                                      backgroundColor: "#22c55e",
                                      border: "2px solid #ffffff",
                                      boxShadow: "0 0 0 1px rgba(34, 197, 94, 0.4)"
                                    }}
                                    title="กำลังปฏิบัติงานอยู่ขณะนี้"
                                  />
                                </div>

                                {/* Name & Role */}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: "800",
                                      fontSize: "1rem",
                                      color: "#0f172a",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis"
                                    }}
                                    title={trainee.name}
                                  >
                                    {trainee.name}
                                  </div>
                                  <div style={{ marginTop: "3px" }}>
                                    <span
                                      style={{
                                        backgroundColor: theme.badgeBg,
                                        color: theme.badgeColor,
                                        border: `1px solid ${theme.badgeBorder}`,
                                        fontSize: "0.75rem",
                                        fontWeight: "700",
                                        padding: "2px 8px",
                                        borderRadius: "10px",
                                        display: "inline-block"
                                      }}
                                    >
                                      {isDoc ? "👨‍⚕️ " : "🎓 "}
                                      {trainee.type}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Live On-Duty Pill */}
                              <span
                                style={{
                                  backgroundColor: "#ecfdf5",
                                  color: "#15803d",
                                  border: "1px solid #86efac",
                                  padding: "3px 9px",
                                  borderRadius: "16px",
                                  fontSize: "0.72rem",
                                  fontWeight: "800",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0
                                }}
                              >
                                🟢 กำลังฝึก
                              </span>
                            </div>

                            {/* Department & Institution Info Box */}
                            <div
                              style={{
                                backgroundColor: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                padding: "10px 12px",
                                marginBottom: "14px"
                              }}
                            >
                              <div className="d-flex align-items-center mb-1" style={{ fontSize: "0.84rem" }}>
                                <LocalHospitalIcon style={{ fontSize: "16px", color: theme.textDark, marginRight: "6px", flexShrink: 0 }} />
                                <span style={{ color: "#64748b", marginRight: "4px" }}>กลุ่มงาน:</span>
                                <strong style={{ color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {trainee.department || "-"}
                                </strong>
                              </div>
                              <div className="d-flex align-items-center" style={{ fontSize: "0.82rem" }}>
                                <SchoolIcon style={{ fontSize: "16px", color: "#64748b", marginRight: "6px", flexShrink: 0 }} />
                                <span style={{ color: "#64748b", marginRight: "4px" }}>สถาบัน:</span>
                                <span style={{ color: "#334155", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {trainee.institution || "-"}
                                </span>
                              </div>
                            </div>

                            {/* Rotation Timeline & Visual Progress Bar */}
                            <div style={{ marginBottom: "12px" }}>
                              <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "0.8rem" }}>
                                <span style={{ color: "#475569", fontWeight: "600" }}>
                                  📅 {trainee.dateRange}
                                </span>
                                {progress.remainingDays <= 7 ? (
                                  <span
                                    style={{
                                      backgroundColor: "#fef2f2",
                                      color: "#b91c1c",
                                      border: "1px solid #fecaca",
                                      fontSize: "0.74rem",
                                      fontWeight: "800",
                                      padding: "2px 7px",
                                      borderRadius: "6px"
                                    }}
                                  >
                                    ⏳ เหลืออีก {progress.remainingDays} วัน
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      backgroundColor: "#f0fdf4",
                                      color: "#15803d",
                                      border: "1px solid #bbf7d0",
                                      fontSize: "0.74rem",
                                      fontWeight: "800",
                                      padding: "2px 7px",
                                      borderRadius: "6px"
                                    }}
                                  >
                                    ⏱️ เหลืออีก {progress.remainingDays} วัน
                                  </span>
                                )}
                              </div>

                              {/* Progress Bar Container */}
                              <div
                                style={{
                                  height: "7px",
                                  backgroundColor: "#e2e8f0",
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  position: "relative"
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${progress.percent}%`,
                                    background: theme.gradient,
                                    borderRadius: "10px",
                                    transition: "width 0.5s ease"
                                  }}
                                />
                              </div>
                              <div className="d-flex justify-content-between mt-1" style={{ fontSize: "0.74rem", color: "#64748b" }}>
                                <span>ผ่านไปแล้ว {progress.elapsedDays} วัน ({progress.percent}%)</span>
                                <span>รวม {progress.totalDays} วัน</span>
                              </div>
                            </div>
                          </div>

                          {/* Card Footer: Dormitory & Action Profile Link */}
                          <div
                            style={{
                              padding: "10px 18px",
                              backgroundColor: "#f8fafc",
                              borderTop: "1px solid #e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between"
                            }}
                          >
                            {/* Dormitory Info */}
                            <div>
                              {trainee.hasDorm ? (
                                <span
                                  style={{
                                    backgroundColor: "#eff6ff",
                                    color: "#1d4ed8",
                                    border: "1px solid #bfdbfe",
                                    padding: "3px 8px",
                                    borderRadius: "8px",
                                    fontSize: "0.76rem",
                                    fontWeight: "700",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                >
                                  <HotelIcon style={{ fontSize: "14px" }} />
                                  {trainee.dormBuilding} ห้อง {trainee.dormRoom}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    backgroundColor: "#f1f5f9",
                                    color: "#475569",
                                    border: "1px solid #cbd5e1",
                                    padding: "3px 8px",
                                    borderRadius: "8px",
                                    fontSize: "0.76rem",
                                    fontWeight: "600"
                                  }}
                                >
                                  🚶 พักภายนอก รพ. (ไป-กลับ)
                                </span>
                              )}
                            </div>

                            {/* Profile Link Button */}
                            <button
                              type="button"
                              onClick={() => history.push(`/trainee/list?search=${encodeURIComponent(trainee.name)}`)}
                              style={{
                                border: "none",
                                background: "none",
                                color: "#0284c7",
                                fontSize: "0.8rem",
                                fontWeight: "800",
                                cursor: "pointer",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                transition: "all 0.15s"
                              }}
                              title="คลิกเพื่อไปดูประวัติและข้อมูลเต็มที่หน้ารายชื่อ"
                            >
                              ดูประวัติ
                              <ArrowForwardIcon style={{ fontSize: "14px" }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Table View Mode */
            <div style={{ overflowX: "auto" }}>
              <Table responsive hover size="sm" style={{ margin: 0, fontSize: "0.9rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", color: "#0f172a", borderBottom: "2px solid #cbd5e1" }}>
                    <th style={{ width: "50px", textAlign: "center", padding: "12px 14px", fontWeight: "800" }}>ลำดับ</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>ผู้ฝึกปฏิบัติงาน</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>ตำแหน่ง / ประเภท</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>สถาบัน / ต้นสังกัด</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>กลุ่มงานที่ฝึกใน รพ.</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>ช่วงเวลาที่ฝึก & ความคืบหน้า</th>
                    <th style={{ width: "230px", padding: "12px 14px", fontWeight: "800" }}>🏢 การพักหอพัก รพ.</th>
                    <th style={{ textAlign: "center", width: "110px", padding: "12px 14px", fontWeight: "800" }}>สถานะ</th>
                    <th style={{ textAlign: "center", width: "90px", padding: "12px 14px", fontWeight: "800" }}>ข้อมูล</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActiveTrainees.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>
                        {selectedYear < currentFiscalYear
                          ? `🏁 สิ้นสุดรอบปีงบประมาณ ${selectedYear} แล้ว — ผู้ฝึกปฏิบัติงานในรอบปีนี้สำเร็จการฝึกครบถ้วนทั้งหมด`
                          : selectedYear > currentFiscalYear
                          ? `⏳ ยังไม่ถึงรอบปีงบประมาณ ${selectedYear} — ยังไม่มีผู้เข้าฝึกประจำการ`
                          : "ไม่มีรายชื่อผู้ฝึกปฏิบัติงานที่ตรงกับเงื่อนไข"}
                      </td>
                    </tr>
                  ) : (
                    filteredActiveTrainees.map((trainee, idx) => {
                      const theme = getDepartmentTheme(trainee.department, trainee.type);
                      const initials = getAvatarInitials(trainee.name);
                      const progress = calculateRotationProgress(trainee.dateRange);

                      return (
                        <tr key={trainee.id || idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ textAlign: "center", color: "#475569", padding: "12px 14px", fontWeight: "700" }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                              <div
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  background: theme.gradient,
                                  color: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "800",
                                  fontSize: "0.85rem",
                                  flexShrink: 0
                                }}
                              >
                                {initials}
                              </div>
                              <div>
                                <div style={{ fontWeight: "800", color: "#0f172a" }}>{trainee.name}</div>
                                <div style={{ fontSize: "0.76rem", color: "#64748b" }}>ID: {trainee.id}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <Badge
                              pill
                              style={{
                                fontSize: "0.78rem",
                                fontWeight: "800",
                                backgroundColor: theme.badgeBg,
                                color: theme.badgeColor,
                                border: `1px solid ${theme.badgeBorder}`
                              }}
                            >
                              {trainee.type}
                            </Badge>
                          </td>
                          <td style={{ fontSize: "0.86rem", color: "#1e293b", fontWeight: "600", padding: "12px 14px" }}>
                            {trainee.institution}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontWeight: "700", color: theme.textDark }}>
                              {trainee.department}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontSize: "0.84rem", fontWeight: "600", color: "#1e293b" }}>
                              {trainee.dateRange}
                            </div>
                            <div className="d-flex align-items-center mt-1" style={{ gap: "8px" }}>
                              <div style={{ flex: 1, height: "6px", backgroundColor: "#e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${progress.percent}%`, background: theme.gradient }} />
                              </div>
                              <span style={{ fontSize: "0.74rem", fontWeight: "700", color: progress.remainingDays <= 7 ? "#dc2626" : "#15803d" }}>
                                {progress.percent}% (เหลือ {progress.remainingDays} วัน)
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            {trainee.hasDorm ? (
                              <span className={classes.roomBadgeDorm}>
                                🏢 {trainee.dormBuilding} ห้อง {trainee.dormRoom}
                              </span>
                            ) : (
                              <span className={classes.roomBadgeNone}>
                                🚶 ไป-กลับ (พักภายนอก รพ.)
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>
                            <span style={{ backgroundColor: "#ecfdf5", color: "#15803d", border: "1px solid #86efac", fontWeight: "800", padding: "4px 10px", borderRadius: "12px", fontSize: "0.8rem" }}>
                              🟢 กำลังฝึก
                            </span>
                          </td>
                          <td style={{ textAlign: "center", padding: "12px 14px" }}>
                            <Button
                              color="primary"
                              simple
                              size="sm"
                              onClick={() => history.push(`/trainee/list?search=${encodeURIComponent(trainee.name)}`)}
                              style={{ padding: "4px 8px", fontSize: "0.8rem", fontWeight: "800" }}
                            >
                              ดูประวัติ ➔
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* 2. ศูนย์วิเคราะห์ข้อมูลและสถิติภาพรวม (Executive Analytics Hub) */}
      <div style={{ marginBottom: "24px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: "10px" }}>
          <div>
            <h4 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.2rem", display: "flex", alignItems: "center" }}>
              <span style={{ display: "inline-flex", padding: "6px 10px", borderRadius: "8px", backgroundColor: "#e0f2fe", color: "#0284c7", marginRight: "10px" }}>
                📊
              </span>
              ศูนย์วิเคราะห์ข้อมูลและสถิติภาพรวม (Executive Analytics Hub)
            </h4>
            <p style={{ margin: "4px 0 0 42px", color: "#475569", fontSize: "0.86rem", fontWeight: "600" }}>
              แนวโน้มรายเดือน, สัดส่วนกลุ่มงาน, แหล่งสถาบันการศึกษา และการกระจายตัวของหลักสูตร/หอพัก ปีงบประมาณ {selectedYear}
            </p>
          </div>
          {/* Status filter toolbar at Hub Header */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#ffffff", padding: "4px 10px", borderRadius: "10px", border: "1px solid #cbd5e1", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
            <span style={{ fontSize: "0.82rem", fontWeight: "800", color: "#334155", marginRight: "4px" }}>
              กรองสถิติ:
            </span>
            <button
              type="button"
              onClick={() => setChartStatusFilter("ACTIVE")}
              style={{
                border: "none",
                backgroundColor: chartStatusFilter === "ACTIVE" ? "#16a34a" : "transparent",
                color: chartStatusFilter === "ACTIVE" ? "#ffffff" : "#166534",
                fontWeight: "700",
                fontSize: "0.8rem",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              🟢 กำลังฝึกอยู่ปัจจุบัน
            </button>
            <button
              type="button"
              onClick={() => setChartStatusFilter("ALL")}
              style={{
                border: "none",
                backgroundColor: chartStatusFilter === "ALL" ? "#0284c7" : "transparent",
                color: chartStatusFilter === "ALL" ? "#ffffff" : "#0369a1",
                fontWeight: "700",
                fontSize: "0.8rem",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              📋 ข้อมูลทั้งหมด (ปีงบ {selectedYear})
            </button>
          </div>
        </div>

        {/* Row 1: Monthly Trends + Department Breakdown */}
        <GridContainer>
          {/* Chart 1: กราฟแท่งแนวโน้มรายเดือน */}
          <GridItem xs={12} lg={7}>
            <div className={classes.modernCard} style={{ height: "100%", marginBottom: "16px" }}>
              <div className={classes.modernCardHeader}>
                <div>
                  <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.02rem" }}>
                    📈 แนวโน้มจำนวนผู้ฝึกงานรายเดือน (ปีงบประมาณไทย ต.ค. - ก.ย.)
                  </h5>
                  <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.82rem", fontWeight: "600" }}>
                    ช่วยวางแผนรองรับช่วงที่มีผู้เข้าฝึกงานหนาแน่น (Peak Period) รายเดือนตลอดปีงบประมาณ
                  </p>
                </div>
              </div>
              <div style={{ padding: "16px 20px" }}>
                <div style={{ height: "240px" }}>
                  <Bar
                    data={barChartData}
                    options={{
                      maintainAspectRatio: false,
                      scales: {
                        yAxes: [{ ticks: { beginAtZero: true, stepSize: 2, fontColor: "#0f172a", fontStyle: "bold" } }],
                        xAxes: [{ ticks: { fontColor: "#0f172a", fontStyle: "bold" } }]
                      },
                      legend: {
                        position: "top",
                        labels: { fontColor: "#0f172a", fontStyle: "bold", fontSize: 12 }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </GridItem>

          {/* Chart 2: กราฟโดนัท สัดส่วนกลุ่มงาน */}
          <GridItem xs={12} lg={5}>
            <div className={classes.modernCard} style={{ height: "100%", marginBottom: "16px" }}>
              <div className={classes.modernCardHeader}>
                <div>
                  <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.02rem" }}>
                    🍩 สัดส่วนผู้ฝึกตามกลุ่มงานของโรงพยาบาล
                    <Badge
                      color={chartStatusFilter === "ACTIVE" ? "success" : "primary"}
                      pill
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.72rem",
                        backgroundColor: chartStatusFilter === "ACTIVE" ? "#dcfce7" : "#e0f2fe",
                        color: chartStatusFilter === "ACTIVE" ? "#15803d" : "#0369a1",
                        border: chartStatusFilter === "ACTIVE" ? "1px solid #86efac" : "1px solid #7dd3fc"
                      }}
                    >
                      {chartStatusFilter === "ACTIVE" ? "🟢 ปัจจุบัน" : "📋 ทั้งปีงบ"}
                    </Badge>
                  </h5>
                  <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.82rem", fontWeight: "600" }}>
                    เชื่อมโยงกับฐานข้อมูลกลุ่มงาน รพ. (dbo.Department)
                  </p>
                </div>
              </div>
              <div style={{ padding: "16px 20px" }}>
                <div style={{ height: "210px" }}>
                  <Doughnut
                    data={doughnutData}
                    options={{
                      maintainAspectRatio: false,
                      legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, fontSize: 11, fontColor: "#0f172a", fontStyle: "bold" }
                      }
                    }}
                  />
                </div>
                <div className="text-center mt-2" style={{ fontSize: "0.84rem", color: "#0f172a", fontWeight: "700" }}>
                  {chartStatusFilter === "ACTIVE" ? (
                    <span>กำลังฝึกปัจจุบัน <b>{filteredChartDoctors.length + filteredChartStudents.length}</b> คน (แพทย์ {filteredChartDoctors.length} คน, นศ. {filteredChartStudents.length} คน)</span>
                  ) : (
                    <span>รวมทั้งสิ้น <b>{dashboardData.currentlyActive?.total || (rawDoctors.length + rawStudents.length)}</b> คน (แพทย์ {dashboardData.currentlyActive?.doctors || rawDoctors.length} คน, นศ. {dashboardData.currentlyActive?.students || rawStudents.length} คน)</span>
                  )}
                </div>
              </div>
            </div>
          </GridItem>
        </GridContainer>

        {/* Row 2: Top Feeder Institutions + Dormitory/Role Distribution */}
        <GridContainer style={{ marginTop: "4px" }}>
          {/* Chart 3: แนวนอน สถาบันการศึกษาและ รพ. ต้นสังกัดชั้นนำ */}
          <GridItem xs={12} lg={7}>
            <div className={classes.modernCard} style={{ height: "100%", marginBottom: "16px" }}>
              <div className={classes.modernCardHeader}>
                <div>
                  <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.02rem" }}>
                    🏛️ สถาบันการศึกษาและ รพ. ต้นสังกัดชั้นนำ (Top Feeder Institutions)
                    <Badge
                      color={chartStatusFilter === "ACTIVE" ? "success" : "primary"}
                      pill
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.72rem",
                        backgroundColor: chartStatusFilter === "ACTIVE" ? "#dcfce7" : "#e0f2fe",
                        color: chartStatusFilter === "ACTIVE" ? "#15803d" : "#0369a1",
                        border: chartStatusFilter === "ACTIVE" ? "1px solid #86efac" : "1px solid #7dd3fc"
                      }}
                    >
                      {chartStatusFilter === "ACTIVE" ? "🟢 ปัจจุบัน" : "📋 ทั้งปีงบ"}
                    </Badge>
                  </h5>
                  <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.82rem", fontWeight: "600" }}>
                    {chartStatusFilter === "ACTIVE"
                      ? "สถาบันของผู้ที่กำลังฝึกปฏิบัติงานจริงในโรงพยาบาลปัจจุบัน"
                      : "มหาวิทยาลัย, สถาบันเฉพาะทาง และโรงเรียนที่มีผู้เข้าฝึกอบรมสูงสุด"}
                  </p>
                </div>
              </div>
              <div style={{ padding: "16px 20px" }}>
                <div style={{ height: "240px" }}>
                  <HorizontalBar
                    data={institutionChartData}
                    options={{
                      maintainAspectRatio: false,
                      scales: {
                        xAxes: [{ ticks: { beginAtZero: true, stepSize: 2, fontColor: "#0f172a", fontStyle: "bold" } }],
                        yAxes: [{ ticks: { fontColor: "#0f172a", fontStyle: "bold", fontSize: 11 } }]
                      },
                      legend: {
                        display: false
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </GridItem>

          {/* Chart 4: สถิติการจัดสรรห้องพักและสายวิชาชีพ */}
          <GridItem xs={12} lg={5}>
            <div className={classes.modernCard} style={{ height: "100%", marginBottom: "16px" }}>
              <div className={classes.modernCardHeader} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "8px" }}>
                <div>
                  <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.02rem" }}>
                    {chartDormView === "BUILDING" ? "🏨 สถิติการจัดสรรหอพัก 4 อาคาร" : "🎓 สัดส่วนตามสายวิชาชีพ / ตำแหน่ง"}
                    <Badge
                      color={chartStatusFilter === "ACTIVE" ? "success" : "primary"}
                      pill
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.72rem",
                        backgroundColor: chartStatusFilter === "ACTIVE" ? "#dcfce7" : "#e0f2fe",
                        color: chartStatusFilter === "ACTIVE" ? "#15803d" : "#0369a1",
                        border: chartStatusFilter === "ACTIVE" ? "1px solid #86efac" : "1px solid #7dd3fc"
                      }}
                    >
                      {chartStatusFilter === "ACTIVE" ? "🟢 ปัจจุบัน" : "📋 ทั้งหมด"}
                    </Badge>
                  </h5>
                  <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.82rem", fontWeight: "600" }}>
                    {chartDormView === "BUILDING"
                      ? `พักใน รพ. ${dormBuildingChartData.dormRate}% (${dormBuildingChartData.totalWithDorm}/${dormBuildingChartData.totalAll} คน)`
                      : `สัดส่วนสายวิชาชีพ (${programRoleChartData.totalCount} คน)`}
                  </p>
                </div>
                {/* Sub-tab & Filter buttons */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                  {/* View Toggle: อาคารหอพัก vs สายวิชาชีพ */}
                  <div style={{ display: "flex", gap: "4px", backgroundColor: "#f1f5f9", padding: "3px", borderRadius: "8px" }}>
                    <button
                      type="button"
                      onClick={() => setChartDormView("BUILDING")}
                      style={{
                        border: "none",
                        backgroundColor: chartDormView === "BUILDING" ? "#0284c7" : "transparent",
                        color: chartDormView === "BUILDING" ? "#ffffff" : "#475569",
                        fontWeight: "700",
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      อาคารหอพัก
                    </button>
                    <button
                      type="button"
                      onClick={() => setChartDormView("ROLE")}
                      style={{
                        border: "none",
                        backgroundColor: chartDormView === "ROLE" ? "#0284c7" : "transparent",
                        color: chartDormView === "ROLE" ? "#ffffff" : "#475569",
                        fontWeight: "700",
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      สายวิชาชีพ
                    </button>
                  </div>

                  {/* Status Toggle: ฝึกปัจจุบัน vs ทั้งหมด */}
                  <div style={{ display: "flex", gap: "3px", backgroundColor: "#f8fafc", padding: "2px", borderRadius: "6px", border: "1px solid #cbd5e1" }}>
                    <button
                      type="button"
                      onClick={() => setChartStatusFilter("ACTIVE")}
                      style={{
                        border: "none",
                        backgroundColor: chartStatusFilter === "ACTIVE" ? "#16a34a" : "transparent",
                        color: chartStatusFilter === "ACTIVE" ? "#ffffff" : "#166534",
                        fontWeight: "700",
                        fontSize: "0.72rem",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      🟢 ฝึกอยู่ปัจจุบัน
                    </button>
                    <button
                      type="button"
                      onClick={() => setChartStatusFilter("ALL")}
                      style={{
                        border: "none",
                        backgroundColor: chartStatusFilter === "ALL" ? "#0f172a" : "transparent",
                        color: chartStatusFilter === "ALL" ? "#ffffff" : "#475569",
                        fontWeight: "700",
                        fontSize: "0.72rem",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      📋 ทั้งหมด
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ padding: "16px 20px" }}>
                <div style={{ height: "210px" }}>
                  <Doughnut
                    data={chartDormView === "BUILDING" ? dormBuildingChartData : programRoleChartData}
                    options={{
                      maintainAspectRatio: false,
                      legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, fontSize: 11, fontColor: "#0f172a", fontStyle: "bold" }
                      }
                    }}
                  />
                </div>
                <div className="text-center mt-2" style={{ fontSize: "0.84rem", color: "#0f172a", fontWeight: "700" }}>
                  {chartDormView === "BUILDING" ? (
                    <span>เข้าพักในหอพัก <b>{dormBuildingChartData.totalWithDorm}</b> คน • พักภายนอก/ไปกลับ <b>{dormBuildingChartData.totalNoDorm}</b> คน</span>
                  ) : (
                    <span>รวมทุกสายวิชาชีพที่บันทึกในระบบ <b>{programRoleChartData.totalCount}</b> คน</span>
                  )}
                </div>
              </div>
            </div>
          </GridItem>
        </GridContainer>
      </div>

      {/* 3. Section: "จะมาอีกทีอีกทีกี่คนตอนไหน" (Upcoming Schedule & Ending Soon) */}
      <GridContainer>
        {/* คอลัมน์ซ้าย: กำหนดการกลุ่มที่จะมาในรอบถัดไป */}
        <GridItem xs={12} lg={8}>
          <div className={`${classes.modernCard} ${classes.cardAccentBorderOrange}`}>
            <div className={classes.modernCardHeader}>
              <div>
                <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.1rem" }}>
                  <EventIcon style={{ verticalAlign: "middle", marginRight: "8px", color: "#ea580c" }} />
                  จะมาอีกทีอีกทีกี่คนตอนไหน (Upcoming Trainee Inflow Schedule)
                </h5>
                <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.88rem", fontWeight: "500" }}>
                  กำหนดการเข้าฝึกงานรอบถัดไป สถาบันต้นสังกัด แผนกที่จะลงฝึก และห้องพักที่จัดสรรล่วงหน้า
                </p>
              </div>
              <Badge color="warning" pill style={{ fontSize: "0.85rem", padding: "6px 14px", backgroundColor: "#ffedd5", color: "#9a3412", fontWeight: "800", border: "1px solid #fb923c" }}>
                {(dashboardData.upcomingIntake || []).length} กลุ่มกำหนดการ
              </Badge>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table responsive hover size="sm" style={{ margin: 0, fontSize: "0.9rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", color: "#0f172a", borderBottom: "2px solid #cbd5e1" }}>
                    <th style={{ padding: "12px 14px", width: "130px", fontWeight: "800" }}>วันที่เริ่มฝึก</th>
                    <th style={{ padding: "12px 14px", width: "100px", textAlign: "center", fontWeight: "800" }}>นับถอยหลัง</th>
                    <th style={{ padding: "12px 14px", width: "80px", textAlign: "center", fontWeight: "800" }}>จำนวน</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>กลุ่ม / รายชื่อผู้ฝึก</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>สถาบันต้นสังกัด</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>กลุ่มงานที่เข้าฝึก</th>
                    <th style={{ padding: "12px 14px", fontWeight: "800" }}>การจัดเตรียมห้องพัก</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData.upcomingIntake || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "26px 20px", color: "#64748b" }}>
                        {selectedYear < currentFiscalYear ? (
                          <div>
                            <span style={{ fontWeight: "700", color: "#475569" }}>🏁 สิ้นสุดปีงบประมาณ {selectedYear} แล้ว</span>
                            <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "4px" }}>ไม่มีกำหนดการรอเข้าฝึกสำหรับปีงบประมาณที่ผ่านมาแล้ว</div>
                          </div>
                        ) : selectedYear > currentFiscalYear ? (
                          <div>
                            <span style={{ fontWeight: "700", color: "#475569" }}>⏳ ยังไม่ถึงรอบปีงบประมาณ {selectedYear}</span>
                            <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "4px" }}>ยังไม่มีกำหนดการหรือการลงทะเบียนล่วงหน้าสำหรับปีนี้</div>
                          </div>
                        ) : (
                          "ไม่มีกำหนดการเข้าฝึกรอบถัดไปสำหรับปีงบประมาณนี้"
                        )}
                      </td>
                    </tr>
                  ) : (
                    (dashboardData.upcomingIntake || []).map((item, idx) => (
                      <tr key={item.id || idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "12px 14px", fontWeight: "800", color: "#0f172a" }}>
                          {item.startDate}
                          <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "600" }}>ถึง {item.endDate}</div>
                        </td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          <Badge
                            color={item.daysUntil <= 14 ? "danger" : item.daysUntil <= 30 ? "warning" : "secondary"}
                            pill
                            style={{ fontSize: "0.8rem", fontWeight: "700" }}
                          >
                            อีก {item.daysUntil} วัน
                          </Badge>
                        </td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          <span style={{ fontWeight: "800", fontSize: "1rem", color: "#0f172a" }}>
                            {item.count}
                          </span>
                          <span style={{ fontSize: "0.8rem", color: "#334155", fontWeight: "600", marginLeft: "3px" }}>คน</span>
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ fontWeight: "800", color: "#0f172a" }}>{item.name}</div>
                          <span style={{ fontSize: "0.8rem", color: item.type === "DOCTOR" ? "#0369a1" : "#15803d", fontWeight: "700" }}>
                            {item.typeName}
                          </span>
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: "0.88rem", color: "#1e293b", fontWeight: "600" }}>
                          {item.institution}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <span style={{ backgroundColor: "#f0fdf4", color: "#14532d", border: "1px solid #86efac", padding: "4px 10px", borderRadius: "6px", fontSize: "0.84rem", fontWeight: "700" }}>
                            {item.department}
                          </span>
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <span className={item.requiresDorm ? classes.roomBadgeDorm : classes.roomBadgeNone}>
                            {item.dormPlan}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: "#f8fafc", borderTop: "1px solid #cbd5e1", fontSize: "0.88rem", color: "#1e293b", fontWeight: "600" }}>
              <span>💡 การจองและสำรองห้องล่วงหน้าช่วยให้จัดตึก 1-4 ได้อย่างเป็นระเบียบ</span>
              {canManage && (
                <Button color="warning" simple size="sm" onClick={() => history.push("/trainee/list")}>
                  จัดการคิวและบันทึกผู้ฝึกกลุ่มใหม่ &raquo;
                </Button>
              )}
            </div>
          </div>
        </GridItem>

        {/* คอลัมน์ขวา: ผู้ที่ใกล้สิ้นสุดการฝึก */}
        <GridItem xs={12} lg={4}>
          <div className={`${classes.modernCard} ${classes.cardAccentBorderTeal}`}>
            <div className={classes.modernCardHeader}>
              <div>
                <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.05rem" }}>
                  <ExitToAppIcon style={{ verticalAlign: "middle", marginRight: "8px", color: "#0284c7" }} />
                  ใกล้สิ้นสุดการฝึก (Ending Soon)
                </h5>
                <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.85rem", fontWeight: "500" }}>
                  เตรียมส่งมอบงานและคืนห้องพักเพื่อรับกลุ่มถัดไป
                </p>
              </div>
              <Badge color="info" pill style={{ fontSize: "0.82rem", fontWeight: "700", backgroundColor: "#e0f2fe", color: "#0369a1" }}>
                {(dashboardData.endingSoon || []).length} ท่าน
              </Badge>
            </div>

            <div style={{ padding: "14px 18px" }}>
              {(dashboardData.endingSoon || []).length === 0 ? (
                <div style={{ textAlign: "center", padding: "26px 14px", color: "#64748b", fontSize: "0.9rem" }}>
                  {selectedYear < currentFiscalYear ? (
                    <div>
                      <div style={{ fontWeight: "700", color: "#475569" }}>✅ สิ้นสุดระยะเวลาการฝึกแล้ว</div>
                      <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "4px" }}>ผู้ฝึกปฏิบัติงานในรอบปีงบ {selectedYear} สำเร็จการฝึกครบถ้วนทั้งหมด</div>
                    </div>
                  ) : selectedYear > currentFiscalYear ? (
                    <div>
                      <div style={{ fontWeight: "700", color: "#475569" }}>⏳ ยังไม่ถึงรอบระยะเวลาการฝึก</div>
                      <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "4px" }}>ปีงบประมาณ {selectedYear} ยังไม่เริ่มดำเนินการ</div>
                    </div>
                  ) : (
                    "ไม่มีผู้ฝึกที่ใกล้สิ้นสุดการฝึกในช่วงนี้"
                  )}
                </div>
              ) : (
                (dashboardData.endingSoon || []).map((item, idx) => (
                  <div
                    key={item.id || idx}
                    style={{
                      borderBottom: idx < (dashboardData.endingSoon || []).length - 1 ? "1px solid #e2e8f0" : "none",
                      padding: "12px 0"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div style={{ fontWeight: "800", color: "#0f172a", fontSize: "0.96rem" }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600" }}>
                          {item.type} • {item.institution}
                        </div>
                      </div>
                      <Badge color={item.daysLeft <= 3 ? "danger" : "warning"} pill style={{ fontSize: "0.78rem", fontWeight: "700" }}>
                        เหลืออีก {item.daysLeft} วัน
                      </Badge>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: "0.86rem" }}>
                      <span style={{ color: "#0369a1", fontWeight: "700" }}>
                        แผนก: {item.department}
                      </span>
                      <span style={{ color: "#991b1b", fontWeight: "800" }}>
                        สิ้นสุด: {item.endDate}
                      </span>
                    </div>

                    <div className="mt-2" style={{ fontSize: "0.85rem", color: "#0f172a", fontWeight: "600" }}>
                      🏢 คืนห้องพัก: <b style={{ color: "#c2410c" }}>{item.room}</b>
                    </div>
                  </div>
                ))
              )}
            </div>

            {canManage && (
              <div className="p-3 text-right" style={{ backgroundColor: "#f8fafc", borderTop: "1px solid #cbd5e1" }}>
                <Button color="info" simple size="sm" onClick={() => history.push("/trainee/dormitory")}>
                  ตรวจสอบผังห้องพักหอพัก &raquo;
                </Button>
              </div>
            )}
          </div>
        </GridItem>
      </GridContainer>

      {/* 4. Section: "คนที่ฝึกปัจจุบันอยู่งานไหน และพักหอไหน ตึกไหน ห้องไหน" */}
      <div className={`${classes.modernCard} ${classes.cardAccentBorderIndigo}`}>
        <div className={classes.modernCardHeader}>
          <div>
            <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.1rem" }}>
              <AssignmentIndIcon style={{ verticalAlign: "middle", marginRight: "8px", color: "#4f46e5" }} />
              ผู้ฝึกปฏิบัติงานปัจจุบัน: ประจำกลุ่มงานไหน และพักหอไหน ตึกไหน ห้องไหน
            </h5>
            <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.88rem", fontWeight: "500" }}>
              จำแนกตามโครงสร้างกลุ่มงานของ รพ. (dbo.Department) และแสดงข้อมูลอาคาร/ชั้น/ห้องพักที่เข้าพักจริง
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="mt-2 mt-md-0">
            <Nav pills style={{ backgroundColor: "#e2e8f0", borderRadius: "24px", padding: "4px" }}>
              <NavItem>
                <NavLink
                  style={{
                    cursor: "pointer",
                    color: activeViewTab === 0 ? "#0f172a" : "#334155",
                    backgroundColor: activeViewTab === 0 ? "#ffffff" : "transparent",
                    borderRadius: "20px",
                    padding: "6px 18px",
                    fontWeight: "800",
                    fontSize: "0.88rem",
                    boxShadow: activeViewTab === 0 ? "0 2px 8px rgba(0,0,0,0.12)" : "none"
                  }}
                  onClick={() => setActiveViewTab(0)}
                >
                  🏢 สรุปรายกลุ่มงาน รพ. ({(dashboardData.departmentBreakdown || []).length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{
                    cursor: "pointer",
                    color: activeViewTab === 1 ? "#0f172a" : "#334155",
                    backgroundColor: activeViewTab === 1 ? "#ffffff" : "transparent",
                    borderRadius: "20px",
                    padding: "6px 18px",
                    fontWeight: "800",
                    fontSize: "0.88rem",
                    boxShadow: activeViewTab === 1 ? "0 2px 8px rgba(0,0,0,0.12)" : "none"
                  }}
                  onClick={() => setActiveViewTab(1)}
                >
                  👥 รายชื่อผู้ฝึกปัจจุบันทุกคน ({(dashboardData.currentTraineesRoster || []).length})
                </NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        {/* TAB 0: สรุปรายกลุ่มงาน */}
        {activeViewTab === 0 && (
          <div style={{ overflowX: "auto" }}>
            <Table responsive hover size="sm" style={{ margin: 0, fontSize: "0.92rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", color: "#0f172a", borderBottom: "2px solid #cbd5e1" }}>
                  <th style={{ width: "60px", textAlign: "center", padding: "12px 14px", fontWeight: "800" }}>ลำดับ</th>
                  <th style={{ width: "120px", padding: "12px 14px", fontWeight: "800" }}>รหัสกลุ่มงาน</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>ชื่อกลุ่มงานในโรงพยาบาล</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>กลุ่มภารกิจ / สายงานสังกัด</th>
                  <th style={{ textAlign: "center", width: "110px", padding: "12px 14px", fontWeight: "800" }}>👨‍⚕️ แพทย์</th>
                  <th style={{ textAlign: "center", width: "110px", padding: "12px 14px", fontWeight: "800" }}>🎓 นักศึกษา</th>
                  <th style={{ textAlign: "center", width: "120px", padding: "12px 14px", fontWeight: "800" }}>👥 รวมผู้ฝึก</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>🏢 หอพัก/ห้องพักหลักที่จัดสรรให้</th>
                </tr>
              </thead>
              <tbody>
                {(dashboardData.departmentBreakdown || []).length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                      ไม่มีข้อมูลกลุ่มงานสำหรับปีงบประมาณนี้
                    </td>
                  </tr>
                ) : (
                  (dashboardData.departmentBreakdown || []).map((dept, idx) => (
                    <tr key={dept.code || idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ textAlign: "center", color: "#334155", padding: "12px 14px", fontWeight: "700" }}>{idx + 1}</td>
                      <td style={{ fontFamily: "monospace", color: "#4f46e5", fontWeight: "800", padding: "12px 14px" }}>
                        {dept.code}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontWeight: "800", color: "#0f172a" }}>{dept.name}</span>
                      </td>
                      <td style={{ fontSize: "0.88rem", color: "#334155", fontWeight: "600", padding: "12px 14px" }}>
                        {dept.groupWorkName || "-"}
                      </td>
                      <td style={{ textAlign: "center", padding: "12px 14px" }}>
                        <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc", padding: "4px 12px", borderRadius: "12px", fontWeight: "800", fontSize: "0.86rem" }}>
                          {dept.doctorCount || 0} คน
                        </span>
                      </td>
                      <td style={{ textAlign: "center", padding: "12px 14px" }}>
                        <span style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "4px 12px", borderRadius: "12px", fontWeight: "800", fontSize: "0.86rem" }}>
                          {dept.studentCount || 0} คน
                        </span>
                      </td>
                      <td style={{ textAlign: "center", padding: "12px 14px" }}>
                        <span style={{ backgroundColor: "#0f172a", color: "#ffffff", padding: "5px 14px", borderRadius: "12px", fontWeight: "800", fontSize: "0.9rem" }}>
                          {dept.total || 0} คน
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span className={classes.roomBadgeDorm}>
                          {dept.dormAssigned || "จัดสรรตามกลุ่มงาน"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* TAB 1: รายชื่อผู้ฝึกงานปัจจุบันทุกคน */}
        {activeViewTab === 1 && (
          <div style={{ overflowX: "auto" }}>
            <Table responsive hover size="sm" style={{ margin: 0, fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", color: "#0f172a", borderBottom: "2px solid #cbd5e1" }}>
                  <th style={{ width: "50px", textAlign: "center", padding: "12px 14px", fontWeight: "800" }}>ลำดับ</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>ชื่อ-สกุล ผู้เข้าฝึก</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>ตำแหน่ง / ประเภท</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>สถาบัน / ต้นสังกัด</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>กลุ่มงานที่ฝึกใน รพ.</th>
                  <th style={{ padding: "12px 14px", fontWeight: "800" }}>ช่วงเวลาที่ฝึก</th>
                  <th style={{ width: "260px", padding: "12px 14px", fontWeight: "800" }}>🏢 ที่พักหอพัก (ตึกไหน / ชั้น / ห้อง)</th>
                  <th style={{ textAlign: "center", width: "110px", padding: "12px 14px", fontWeight: "800" }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {(dashboardData.currentTraineesRoster || []).length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                      ไม่มีรายชื่อผู้ฝึกปฏิบัติงานปัจจุบันในปีงบประมาณนี้
                    </td>
                  </tr>
                ) : (
                  (dashboardData.currentTraineesRoster || []).map((trainee, idx) => (
                    <tr key={trainee.id || idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ textAlign: "center", color: "#334155", padding: "12px 14px", fontWeight: "700" }}>{idx + 1}</td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: "800", color: "#0f172a" }}>{trainee.name}</div>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Badge
                          color={(trainee.type || "").includes("แพทย์") ? "info" : "success"}
                          pill
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: "800",
                            backgroundColor: (trainee.type || "").includes("แพทย์") ? "#e0f2fe" : "#dcfce7",
                            color: (trainee.type || "").includes("แพทย์") ? "#0369a1" : "#15803d",
                            border: (trainee.type || "").includes("แพทย์") ? "1px solid #7dd3fc" : "1px solid #86efac"
                          }}
                        >
                          {trainee.type}
                        </Badge>
                      </td>
                      <td style={{ fontSize: "0.88rem", color: "#1e293b", fontWeight: "600", padding: "12px 14px" }}>
                        {trainee.institution}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontWeight: "700", color: "#0f766e" }}>
                          {trainee.department}
                        </span>
                      </td>
                      <td style={{ fontSize: "0.86rem", color: "#334155", fontWeight: "600", padding: "12px 14px" }}>
                        {trainee.dateRange}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        {trainee.hasDorm ? (
                          <span className={classes.roomBadgeDorm}>
                            🏢 {trainee.dormBuilding} ชั้น {trainee.dormFloor} ห้อง {trainee.dormRoom}
                          </span>
                        ) : (
                          <span className={classes.roomBadgeNone}>
                            🚶 ไม่ได้พักหอพัก (ไป-กลับ)
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: "center", padding: "12px 14px" }}>
                        <span style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac", fontWeight: "800", padding: "4px 10px", borderRadius: "12px", fontSize: "0.8rem" }}>
                          กำลังฝึก
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: "#f8fafc", borderTop: "1px solid #cbd5e1", fontSize: "0.88rem", color: "#0f172a", fontWeight: "700" }}>
          <span>
            💡 สรุปการพัก: ผู้ฝึกงานทั้งหมด {dashboardData.currentlyActive?.total || 0} คน พักหอพัก รพ. {dashboardData.currentlyActive?.withDorm || 0} คน (ไป-กลับ {dashboardData.currentlyActive?.withoutDorm || 0} คน)
          </span>
          {canManage && (
            <Button color="primary" simple size="sm" onClick={() => history.push("/trainee/list")}>
              ดูทะเบียนประวัติผู้ฝึกทั้งหมด &raquo;
            </Button>
          )}
        </div>
      </div>

      {/* 5. Section: ข้อมูลห้องพักสำหรับผู้ที่ขอห้องพักไว้ (เน้นแค่ห้องที่พัก ตึก/ชั้น/ห้อง ไม่บอกว่าเหลืออีกกี่ห้อง) */}
      <div className={`${classes.modernCard} ${classes.cardAccentBorderAmber}`}>
        <div className={classes.modernCardHeader}>
          <div>
            <div className="d-flex align-items-center">
              <HotelIcon style={{ marginRight: "8px", color: "#d97706", fontSize: "1.6rem" }} />
              <h5 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.1rem" }}>
                ข้อมูลห้องพักสำหรับผู้ที่ขอห้องพักไว้ (จัดสรรตาม ตึก / ชั้น / ห้อง)
              </h5>
            </div>
            <p style={{ margin: "4px 0 0", color: "#334155", fontSize: "0.88rem", fontWeight: "600" }}>
              แสดงรายชื่อแพทย์และนักศึกษาที่เข้าพักจริงในแต่ละอาคารและชั้น (ไม่นับรวมผู้ฝึกที่ไป-กลับ)
            </p>
          </div>

          <div>
            {canManage && (
              <Button
                color="warning"
                size="sm"
                onClick={() => history.push("/trainee/dormitory")}
                style={{ fontWeight: "800", backgroundColor: "#ea580c", borderRadius: "8px" }}
              >
                จัดการผังห้องพัก &raquo;
              </Button>
            )}
          </div>
        </div>

        {/* แถบตัวกรอง: สถานะการพัก (ปัจจุบัน vs ประวัติ) และ เลือกตึก */}
        <div style={{ padding: "14px 24px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "14px" }}>
          {/* ตัวกรองสถานะ: กำลังพักปัจจุบัน vs ที่ผ่านมาแล้ว/ประวัติ vs ทั้งหมด */}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
            <span style={{ fontSize: "0.88rem", fontWeight: "800", color: "#1e293b", marginRight: "4px" }}>
              สถานะการพัก:
            </span>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                onClick={() => setDormStatusFilter("ACTIVE")}
                style={{
                  backgroundColor: dormStatusFilter === "ACTIVE" ? "#16a34a" : "#ffffff",
                  color: dormStatusFilter === "ACTIVE" ? "#ffffff" : "#15803d",
                  borderColor: dormStatusFilter === "ACTIVE" ? "#16a34a" : "#86efac",
                  fontWeight: "800",
                  fontSize: "0.83rem",
                  padding: "6px 14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                className="btn btn-outline-success"
              >
                <span>🟢 คนที่พักอยู่ปัจจุบัน</span>
                <span style={{
                  backgroundColor: dormStatusFilter === "ACTIVE" ? "rgba(255,255,255,0.3)" : "#dcfce7",
                  color: dormStatusFilter === "ACTIVE" ? "#ffffff" : "#166534",
                  padding: "1px 7px",
                  borderRadius: "10px",
                  fontSize: "0.75rem",
                  fontWeight: "800"
                }}>
                  {dormCounts.active}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setDormStatusFilter("PAST")}
                style={{
                  backgroundColor: dormStatusFilter === "PAST" ? "#475569" : "#ffffff",
                  color: dormStatusFilter === "PAST" ? "#ffffff" : "#475569",
                  borderColor: dormStatusFilter === "PAST" ? "#475569" : "#cbd5e1",
                  fontWeight: "800",
                  fontSize: "0.83rem",
                  padding: "6px 14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                className="btn btn-outline-secondary"
              >
                <span>🚪 ที่ผ่านมาแล้ว (ดูประวัติ)</span>
                <span style={{
                  backgroundColor: dormStatusFilter === "PAST" ? "rgba(255,255,255,0.3)" : "#f1f5f9",
                  color: dormStatusFilter === "PAST" ? "#ffffff" : "#334155",
                  padding: "1px 7px",
                  borderRadius: "10px",
                  fontSize: "0.75rem",
                  fontWeight: "800"
                }}>
                  {dormCounts.past}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setDormStatusFilter("ALL")}
                style={{
                  backgroundColor: dormStatusFilter === "ALL" ? "#0284c7" : "#ffffff",
                  color: dormStatusFilter === "ALL" ? "#ffffff" : "#0284c7",
                  borderColor: dormStatusFilter === "ALL" ? "#0284c7" : "#bae6fd",
                  fontWeight: "800",
                  fontSize: "0.83rem",
                  padding: "6px 14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                className="btn btn-outline-info"
              >
                <span>📋 ทั้งหมด</span>
                <span style={{
                  backgroundColor: dormStatusFilter === "ALL" ? "rgba(255,255,255,0.3)" : "#e0f2fe",
                  color: dormStatusFilter === "ALL" ? "#ffffff" : "#0369a1",
                  padding: "1px 7px",
                  borderRadius: "10px",
                  fontSize: "0.75rem",
                  fontWeight: "800"
                }}>
                  {dormCounts.total}
                </span>
              </button>
            </div>
          </div>

          {/* ตัวกรองเลือกตึก */}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "6px" }}>
            <span style={{ fontSize: "0.86rem", fontWeight: "700", color: "#0f172a", marginRight: "4px" }}>
              เลือกตึก:
            </span>
            <div className="btn-group btn-group-sm" role="group">
              {[
                { id: "ALL", label: "ทุกตึก" },
                { id: "1", label: "ตึก 1 (นศ.)" },
                { id: "2", label: "ตึก 2 (แพทย์)" },
                { id: "3", label: "ตึก 3 (พยาบาล/หญิง)" },
                { id: "4", label: "ตึก 4 (รับรอง)" }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSelectedBuildingTab(tab.id)}
                  style={{
                    backgroundColor: selectedBuildingTab === tab.id ? "#0f172a" : "#ffffff",
                    color: selectedBuildingTab === tab.id ? "#ffffff" : "#334155",
                    borderColor: "#cbd5e1",
                    fontWeight: "800",
                    fontSize: "0.82rem",
                    padding: "5px 12px",
                    cursor: "pointer"
                  }}
                  className="btn btn-outline-secondary"
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* รายการห้องพักที่มีผู้เข้าพักจริง (แยกตามตึกและชั้น) */}
        <div style={{ padding: "20px 24px" }}>
          <GridContainer>
            {filteredBuildings.map(b => (
              <GridItem xs={12} md={selectedBuildingTab === "ALL" ? 6 : 12} key={b.buildingNo}>
                <div className={classes.buildingPanel}>
                  {/* หัวตึก */}
                  <div className={classes.buildingPanelHeader}>
                    <div>
                      <h6 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1rem" }}>
                        🏢 {b.buildingName}
                      </h6>
                      <span style={{ fontSize: "0.8rem", color: "#334155", fontWeight: "600" }}>
                        กลุ่มผู้พัก: {b.primaryGroup}
                      </span>
                    </div>
                    <Badge color="dark" pill style={{
                      fontSize: "0.84rem",
                      padding: "6px 12px",
                      backgroundColor: b.residentsCount > 0
                        ? (dormStatusFilter === "ACTIVE" ? "#16a34a" : "#0f172a")
                        : "#64748b"
                    }}>
                      ผู้พัก {b.residentsCount} คน
                    </Badge>
                  </div>

                  {/* ชั้นและห้องที่มีคนพัก */}
                  <div>
                    {b.floors.length === 0 ? (
                      <div style={{ padding: "32px 16px", textAlign: "center", color: "#64748b", backgroundColor: "#f8fafc", borderRadius: "8px", margin: "14px" }}>
                        <HotelIcon style={{ fontSize: "2.4rem", color: "#cbd5e1", marginBottom: "8px" }} />
                        <div style={{ fontWeight: "800", fontSize: "0.92rem", color: "#475569" }}>
                          {dormStatusFilter === "ACTIVE"
                            ? "ไม่มีผู้พักอาศัยปัจจุบันในอาคารนี้"
                            : dormStatusFilter === "PAST"
                            ? "ไม่มีประวัติการพักในอาคารนี้"
                            : "ไม่มีข้อมูลการพักในอาคารนี้"}
                        </div>
                        {dormStatusFilter === "ACTIVE" && (
                          <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: "4px" }}>
                            สามารถคลิกเลือกแท็บ &ldquo;ที่ผ่านมาแล้ว (ดูประวัติ)&rdquo; ด้านบน เพื่อดูผู้เคยเข้าพัก
                          </div>
                        )}
                      </div>
                    ) : (
                      b.floors.map(fl => (
                        <div key={fl.floorNo}>
                          <div className={classes.floorHeader}>
                            <MeetingRoomIcon style={{ fontSize: "18px", marginRight: "6px", color: "#0284c7" }} />
                            {fl.floorTitle}
                          </div>
                          <div style={{ padding: "8px 12px" }}>
                            {fl.rooms.map((rm, rIdx) => {
                              const isAct = isStayActive(rm);
                              return (
                                <div
                                  key={rIdx}
                                  className={classes.roomItemCard}
                                  style={{
                                    borderLeft: isAct ? "4px solid #16a34a" : "4px solid #94a3b8",
                                    backgroundColor: isAct ? "#ffffff" : "#f8fafc"
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                      {/* ป้ายเลขห้องเด่นชัด */}
                                      <span style={{
                                        backgroundColor: isAct ? "#0f172a" : "#475569",
                                        color: "#ffffff",
                                        fontWeight: "900",
                                        fontSize: "0.95rem",
                                        padding: "4px 10px",
                                        borderRadius: "6px",
                                        marginRight: "12px",
                                        letterSpacing: "0.5px"
                                      }}>
                                        ห้อง {rm.roomNumber}
                                      </span>
                                      <div>
                                        <div style={{ fontWeight: "800", color: "#0f172a", fontSize: "0.95rem" }}>
                                          👤 {rm.residentName}
                                        </div>
                                        <div style={{ fontSize: "0.82rem", color: "#334155", fontWeight: "600" }}>
                                          {rm.roleTitle} • <span style={{ color: "#0f766e" }}>{rm.department}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="text-right">
                                      {isAct ? (
                                        <span style={{
                                          backgroundColor: rm.status === "จัดสรรล่วงหน้า" ? "#ffedd5" : "#dcfce7",
                                          color: rm.status === "จัดสรรล่วงหน้า" ? "#9a3412" : "#15803d",
                                          border: rm.status === "จัดสรรล่วงหน้า" ? "1px solid #fb923c" : "1px solid #86efac",
                                          padding: "3px 10px",
                                          borderRadius: "6px",
                                          fontSize: "0.78rem",
                                          fontWeight: "800",
                                          display: "inline-block"
                                        }}>
                                          🟢 {rm.status || "กำลังพักอาศัย"}
                                        </span>
                                      ) : (
                                        <span style={{
                                          backgroundColor: "#f1f5f9",
                                          color: "#475569",
                                          border: "1px solid #cbd5e1",
                                          padding: "3px 10px",
                                          borderRadius: "6px",
                                          fontSize: "0.78rem",
                                          fontWeight: "800",
                                          display: "inline-block"
                                        }}>
                                          🚪 ออกไปแล้ว (ประวัติ)
                                        </span>
                                      )}
                                      <div style={{ fontSize: "0.78rem", color: "#475569", fontWeight: "600", marginTop: "4px" }}>
                                        {rm.dateRange}
                                      </div>
                                    </div>
                                  </div>

                                  {rm.note && (
                                    <div style={{ marginTop: "6px", fontSize: "0.78rem", color: "#b45309", fontWeight: "700", backgroundColor: "#fffbeb", padding: "4px 8px", borderRadius: "4px", borderLeft: "3px solid #f59e0b" }}>
                                      {rm.note}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </GridItem>
            ))}
          </GridContainer>
        </div>
      </div>
    </div>
  )}

  {/* ========================================================================= */}
  {/* FINANCIAL DASHBOARD MODE (Dashboard การเงินและค่าหอพัก)                     */}
  {/* ========================================================================= */}
  {SHOW_FINANCIAL_TAB && dashboardMode === "FINANCIAL" && (
    <div>
      {/* 1. Top 4 Financial KPI Stat Cards */}
      <GridContainer>
        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <div style={{ backgroundColor: "#e0f2fe", border: "1px solid #7dd3fc", color: "#0369a1", borderRadius: "12px", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AttachMoneyIcon style={{ fontSize: "26px" }} />
                </div>
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                    ยอดเรียกเก็บทั้งหมด
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: "600" }}>
                    Total Billed / Revenue
                  </div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: "2.2rem", fontWeight: "800", color: "#0f172a", lineHeight: 1.1 }}>
              {financialData.totalExpectedRevenue.toLocaleString()} <span style={{ fontSize: "1rem", fontWeight: "600", color: "#64748b" }}>บาท</span>
            </div>
            <div className="d-flex align-items-center mt-2" style={{ fontSize: "0.82rem", color: "#0369a1", fontWeight: "700" }}>
              👥 ผู้เข้าพักรวม {financialData.totalResidentsCount} รายการ (ปีงบ {selectedYear})
            </div>
          </div>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <div style={{ backgroundColor: "#dcfce7", border: "1px solid #86efac", color: "#15803d", borderRadius: "12px", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircleIcon style={{ fontSize: "26px" }} />
                </div>
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                    จัดเก็บได้แล้ว
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: "600" }}>
                    Collected ({financialData.collectionRate}%)
                  </div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: "2.2rem", fontWeight: "800", color: "#166534", lineHeight: 1.1 }}>
              {financialData.totalCollected.toLocaleString()} <span style={{ fontSize: "1rem", fontWeight: "600", color: "#64748b" }}>บาท</span>
            </div>
            <div className="d-flex align-items-center mt-2" style={{ fontSize: "0.82rem", color: "#166534", fontWeight: "700" }}>
              ✓ ชำระครบถ้วนแล้ว {financialData.fullyPaidCount} คน
            </div>
          </div>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard} style={{ borderLeft: "5px solid #dc2626" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <div style={{ backgroundColor: "#fee2e2", border: "1px solid #fca5a5", color: "#b91c1c", borderRadius: "12px", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <WarningIcon style={{ fontSize: "26px" }} />
                </div>
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                    ยอดค้างชำระทั้งหมด
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#b91c1c", fontWeight: "700" }}>
                    ⚠️ การเงิน/บริหารต้องทวงถาม
                  </div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: "2.2rem", fontWeight: "800", color: "#dc2626", lineHeight: 1.1 }}>
              {financialData.totalOverdue.toLocaleString()} <span style={{ fontSize: "1rem", fontWeight: "600", color: "#64748b" }}>บาท</span>
            </div>
            <div className="d-flex align-items-center mt-2" style={{ fontSize: "0.82rem", color: "#dc2626", fontWeight: "800" }}>
              ⚠️ มียอดค้างชำระ {financialData.overdueResidentsCount} คน
            </div>
          </div>
        </GridItem>

        <GridItem xs={12} sm={6} md={3}>
          <div className={classes.statCard}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <div style={{ backgroundColor: "#fef3c7", border: "1px solid #fcd34d", color: "#b45309", borderRadius: "12px", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PeopleIcon style={{ fontSize: "26px" }} />
                </div>
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                    ผู้เข้าพักที่ค้างชำระ
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: "600" }}>
                    Overdue Residents
                  </div>
                </div>
              </div>
            </div>
            <div style={{ fontSize: "2.2rem", fontWeight: "800", color: "#b45309", lineHeight: 1.1 }}>
              {financialData.overdueResidentsCount} <span style={{ fontSize: "1rem", fontWeight: "600", color: "#64748b" }}>คน</span>
            </div>
            <div className="d-flex align-items-center mt-2" style={{ fontSize: "0.82rem", color: "#b45309", fontWeight: "700" }}>
              📋 ติดตามรายชื่อในคิวทวงถามด้านล่าง
            </div>
          </div>
        </GridItem>
      </GridContainer>

      {/* 2. Resident & Room Payment Board (ตารางสรุปการจ่ายเงินค่าหอพักรายคนและรายห้อง) */}
      <div className="mb-4">
        <div style={{ backgroundColor: "#ffffff", borderRadius: "14px", border: "1px solid #cbd5e1", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ backgroundColor: "#f0fdf4", borderBottom: "2px solid #bbf7d0", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h5 style={{ margin: 0, fontWeight: "800", color: "#166534", fontSize: "1.1rem" }}>
                <HotelIcon style={{ verticalAlign: "middle", marginRight: "8px", color: "#16a34a" }} />
                ตารางสรุปการจ่ายเงินค่าหอพักรายคนและรายห้อง (Resident & Room Payment Board)
              </h5>
              <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "#14532d" }}>
                ตรวจสอบยอดเงินค่าหอพักของผู้เข้าพักทุกคน แยกตามห้องพักและอาคาร (ปีงบประมาณ {selectedYear} ทั้งหมด {financialData.totalResidentsCount ?? residentFilterCounts.all} คน • ชำระแล้ว {financialData.fullyPaidCount ?? residentFilterCounts.paid} คน, ค้างชำระ {financialData.overdueResidentsCount ?? residentFilterCounts.overdue} คน)
              </p>
            </div>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
              <span style={{ backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "700" }}>
                ✓ จ่ายครบแล้ว {financialData.fullyPaidCount ?? residentFilterCounts.paid} คน
              </span>
              <span style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "4px 10px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "800" }}>
                ⏳ ค้างชำระ {financialData.overdueResidentsCount ?? residentFilterCounts.overdue} คน
              </span>
              <Button
                color="success"
                size="sm"
                style={{ fontWeight: "bold", marginLeft: "6px" }}
                onClick={() => history.push("/trainee/dormitory")}
              >
                <AttachMoneyIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                ระบบบันทึกรับเงิน
              </Button>
            </div>
          </div>

          {/* Filter & Search Toolbar */}
          <div style={{ padding: "12px 20px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <div className="d-flex flex-wrap align-items-center" style={{ gap: "6px" }}>
              <span style={{ fontSize: "0.82rem", fontWeight: "800", color: "#475569", marginRight: "4px" }}>
                ตัวกรอง:
              </span>
              {[
                { key: "ALL", label: `ทั้งหมด (${residentFilterCounts.all} คน)` },
                { key: "PAID", label: `✓ จ่ายครบแล้ว (${residentFilterCounts.paid} คน)`, color: "#166534", bg: "#dcfce7" },
                { key: "OVERDUE", label: `⏳ ค้างชำระ (${residentFilterCounts.overdue} คน)`, color: "#991b1b", bg: "#fee2e2" },
                { key: "B1", label: `🏢 ตึก 1 (${residentFilterCounts.b1} คน)` },
                { key: "B2", label: `🏢 ตึก 2 (${residentFilterCounts.b2} คน)` },
                { key: "B4", label: `🏢 ตึก 4 (${residentFilterCounts.b4} คน)` }
              ].map(btn => (
                <button
                  key={btn.key}
                  type="button"
                  className="btn btn-sm"
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: paymentRoomFilter === btn.key ? "800" : "600",
                    backgroundColor: paymentRoomFilter === btn.key ? (btn.color ? btn.color : "#0f172a") : "#ffffff",
                    color: paymentRoomFilter === btn.key ? "#ffffff" : "#334155",
                    borderColor: paymentRoomFilter === btn.key ? "transparent" : "#cbd5e1",
                    padding: "4px 10px",
                    borderRadius: "6px"
                  }}
                  onClick={() => setPaymentRoomFilter(btn.key)}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            <div style={{ minWidth: "260px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="🔍 ค้นหาชื่อผู้พัก, เลขห้อง, สถาบัน..."
                value={paymentSearch}
                onChange={(e) => setPaymentSearch(e.target.value)}
                style={{ fontSize: "0.82rem", borderRadius: "6px" }}
              />
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f1f5f9", color: "#334155", borderBottom: "2px solid #cbd5e1" }}>
                  <th style={{ padding: "10px 12px", textAlign: "center", width: "45px" }}>#</th>
                  <th style={{ padding: "10px 12px", textAlign: "center" }}>ห้อง & อาคาร</th>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>ผู้เข้าพัก & บทบาท</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>ช่วงเวลาเข้าพัก</th>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>สูตรและเรทค่าหอพัก</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>ยอดเรียกเก็บ</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", color: "#166534" }}>จ่ายแล้ว</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", color: "#dc2626" }}>คงค้าง</th>
                  <th style={{ padding: "10px 12px", textAlign: "center" }}>สถานะการเงิน</th>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>หมายเหตุ / ติดตาม / หลักฐาน</th>
                </tr>
              </thead>
              <tbody>
                {residentPaymentList.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
                      ไม่พบข้อมูลผู้เข้าพักตามเงื่อนไขที่เลือก
                    </td>
                  </tr>
                ) : (
                  residentPaymentList.map((item, idx) => {
                    const isPaid = item.paymentStatus === "PAID" || item.remainingBalance <= 0;
                    const isOverdue = item.paymentStatus === "OVERDUE" || item.remainingBalance > 0;
                    const bBg = item.buildingNo === "1" ? "#e0f2fe" : item.buildingNo === "2" ? "#f3e8ff" : "#fef3c7";
                    const bColor = item.buildingNo === "1" ? "#0369a1" : item.buildingNo === "2" ? "#6b21a8" : "#92400e";

                    return (
                      <tr
                        key={item.id || idx}
                        style={{
                          borderBottom: "1px solid #e2e8f0",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fbfcfe"
                        }}
                      >
                        <td style={{ padding: "10px 12px", textAlign: "center", color: "#64748b", fontWeight: "600" }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <span style={{ backgroundColor: bBg, color: bColor, padding: "4px 8px", borderRadius: "6px", fontWeight: "800", fontSize: "0.82rem", border: `1px solid ${bColor}33` }}>
                            {item.buildingName} ห้อง {item.roomNumber}
                          </span>
                          <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: "2px" }}>
                            ชั้น {item.floor}
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: "800", color: "#0f172a" }}>
                            {item.traineeName}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "#475569" }}>
                            <span className="badge" style={{ backgroundColor: item.traineeType === "DOCTOR" ? "#e0f2fe" : "#dcfce7", color: item.traineeType === "DOCTOR" ? "#0369a1" : "#15803d", marginRight: "4px" }}>
                              {item.role}
                            </span>
                            {item.institution}
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          <div style={{ color: "#334155", fontWeight: "600" }}>
                            {item.dateRange}
                          </div>
                          {item.isCurrentStay && (
                            <span style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: "4px", padding: "1px 6px", fontSize: "0.72rem", fontWeight: "700", display: "inline-block", marginTop: "2px" }}>
                              🟢 กำลังพักอาศัย
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ backgroundColor: "#f1f5f9", color: "#334155", padding: "3px 8px", borderRadius: "4px", fontSize: "0.78rem", fontWeight: "600", display: "inline-block" }}>
                            {item.prorationFormula}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", color: "#0f172a" }}>
                          {Number(item.feeAmount || 0).toLocaleString()} บ.
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "700", color: "#166534" }}>
                          {Number(item.paidAmount || 0).toLocaleString()} บ.
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: "800", color: isOverdue ? "#dc2626" : "#64748b", backgroundColor: isOverdue ? "#fef2f2" : "transparent" }}>
                          {isOverdue ? `${Number(item.remainingBalance || 0).toLocaleString()} บ.` : "0 บ."}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <div>
                            {isPaid ? (
                              <span style={{ backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac", borderRadius: "8px", padding: "4px 8px", fontSize: "0.8rem", fontWeight: "800", display: "inline-flex", alignItems: "center" }}>
                                ✓ จ่ายครบแล้ว
                              </span>
                            ) : (
                              <span style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: "8px", padding: "4px 8px", fontSize: "0.8rem", fontWeight: "800", display: "inline-flex", alignItems: "center" }}>
                                ⏳ ค้างชำระ
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPaymentModal(item)}
                            className="btn btn-sm"
                            style={{
                              backgroundColor: isOverdue ? "#dc2626" : "#0284c7",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.72rem",
                              fontWeight: "bold",
                              marginTop: "4px",
                              cursor: "pointer"
                            }}
                            title="คลิกเพื่อบันทึกหรืออัพเดทสถานะการเงิน"
                          >
                            💳 อัพเดท
                          </button>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          {item.remarks && (
                            <div style={{ fontSize: "0.8rem", color: isOverdue ? "#b91c1c" : "#475569", fontWeight: isOverdue ? "700" : "500" }}>
                              {item.remarks}
                            </div>
                          )}
                          {item.receiptNo && item.receiptNo !== "-" && (
                            <div style={{ fontSize: "0.74rem", color: "#0369a1", fontWeight: "600", marginTop: "2px" }}>
                              🧾 {item.receiptNo} {item.paidDate && item.paidDate !== "-" ? `(${item.paidDate})` : ""}
                            </div>
                          )}
                          {isOverdue && item.phone && item.phone !== "-" && (
                            <div style={{ marginTop: "2px" }}>
                              <a href={`tel:${item.phone}`} style={{ fontSize: "0.76rem", color: "#0284c7", fontWeight: "700", textDecoration: "none" }}>
                                📞 {item.phone}
                              </a>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {/* Summary Footer */}
              <tfoot>
                <tr style={{ backgroundColor: "#f8fafc", borderTop: "2px solid #cbd5e1", fontWeight: "800", color: "#0f172a" }}>
                  <td colSpan="5" style={{ padding: "12px 14px", textAlign: "right" }}>
                    รวมยอดตามรายการที่แสดง ({filteredPaymentTotals.count} รายการ) :
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "right", fontSize: "0.95rem" }}>
                    {filteredPaymentTotals.expected.toLocaleString()} บ.
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "right", color: "#166534", fontSize: "0.95rem" }}>
                    {filteredPaymentTotals.paid.toLocaleString()} บ.
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "right", color: "#dc2626", fontSize: "0.95rem", backgroundColor: filteredPaymentTotals.overdue > 0 ? "#fee2e2" : "transparent" }}>
                    {filteredPaymentTotals.overdue.toLocaleString()} บ.
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "center" }}>
                    <span style={{ backgroundColor: "#dcfce7", color: "#166534", padding: "3px 8px", borderRadius: "6px", fontSize: "0.82rem" }}>
                      {filteredPaymentTotals.rate}%
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "0.8rem", color: "#64748b" }}>
                    {filteredPaymentTotals.overdue === 0 ? "✓ ชำระครบถ้วนทั้งหมด" : `⚠️ มียอดค้างชำระ ${filteredPaymentTotals.overdue.toLocaleString()} บาท`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* 3. Overdue Action Queue (รายการแจ้งเตือนค้างชำระเร่งด่วนสำหรับการเงินและบริหาร) */}
      <div className="mb-4">
        <div style={{ backgroundColor: "#ffffff", borderRadius: "14px", border: "1px solid #cbd5e1", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ backgroundColor: "#fef2f2", borderBottom: "2px solid #fecaca", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h5 style={{ margin: 0, fontWeight: "800", color: "#991b1b", fontSize: "1.05rem" }}>
                <WarningIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />
                คิวแจ้งเตือนติดตามทวงถามค่าหอพัก (Overdue Action Queue)
              </h5>
              <p style={{ margin: "4px 0 0", fontSize: "0.84rem", color: "#7f1d1d" }}>
                รายชื่อผู้เข้าพักที่มียอดค้างชำระ สำหรับฝ่ายการเงินและกลุ่มบริหารงานทั่วไปใช้ทวงเงินน้องรายเดือน
              </p>
            </div>
            <div style={{ marginTop: "6px" }}>
              <Button
                color="success"
                size="sm"
                style={{ fontWeight: "bold" }}
                onClick={() => history.push("/trainee/dormitory")}
              >
                <AttachMoneyIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                ไปตารางบันทึกรับเงิน (Checklist Matrix)
              </Button>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", color: "#334155", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>ผู้เข้าพัก & สถาบัน</th>
                  <th style={{ padding: "10px 10px", textAlign: "center" }}>ห้องพัก</th>
                  <th style={{ padding: "10px 10px", textAlign: "left" }}>ช่วงเวลาเข้าพัก</th>
                  <th style={{ padding: "10px 10px", textAlign: "right" }}>ยอดเรียกเก็บ</th>
                  <th style={{ padding: "10px 10px", textAlign: "right" }}>จ่ายแล้ว</th>
                  <th style={{ padding: "10px 10px", textAlign: "right", color: "#dc2626" }}>ยอดค้างชำระ</th>
                  <th style={{ padding: "10px 10px", textAlign: "center" }}>เบอร์โทรทวงถาม</th>
                  <th style={{ padding: "10px 10px", textAlign: "left" }}>สถานะการทวงถาม</th>
                  <th style={{ padding: "10px 14px", textAlign: "center" }}>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {!(financialData.overdueQueue && financialData.overdueQueue.length > 0) ? (
                  <tr>
                    <td colSpan="8" style={{ padding: "30px", textAlign: "center", color: "#166534", fontWeight: "700" }}>
                      ✓ ไม่มีรายการค้างชำระในปีงบประมาณ {selectedYear} (จัดเก็บเรียบร้อยครบถ้วน)
                    </td>
                  </tr>
                ) : (
                  financialData.overdueQueue.map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: "1px solid #e2e8f0", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fffbfb" }}>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ fontWeight: "700", color: "#0f172a" }}>{item.name}</div>
                      <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{item.role} • {item.institution}</div>
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", padding: "4px 8px", borderRadius: "6px", fontWeight: "700", fontSize: "0.82rem" }}>
                        {item.room}
                      </span>
                    </td>
                    <td style={{ padding: "10px 10px", color: "#334155" }}>
                      {item.dateRange}
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "right", fontWeight: "700", color: "#0f172a" }}>
                      {item.dueAmount.toLocaleString()} บ.
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "right", fontWeight: "700", color: "#166534" }}>
                      {item.paidAmount.toLocaleString()} บ.
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "right", fontWeight: "800", color: "#dc2626", backgroundColor: "#fef2f2" }}>
                      {item.overdueAmount.toLocaleString()} บาท
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <a href={`tel:${item.phone}`} style={{ color: "#0284c7", fontWeight: "700", textDecoration: "none" }}>
                        📞 {item.phone}
                      </a>
                    </td>
                    <td style={{ padding: "10px 10px" }}>
                      <span className={`badge badge-${item.statusBadgeColor}`} style={{ fontSize: "0.78rem", padding: "4px 8px" }}>
                        {item.statusText}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <Button
                          color="success"
                          size="sm"
                          style={{ fontSize: "0.8rem", padding: "4px 10px", fontWeight: "bold" }}
                          onClick={() => handleOpenPaymentModal(item)}
                          title="บันทึกชำระเงินและหักลบยอดค้างชำระ"
                        >
                          💳 บันทึกชำระ
                        </Button>
                        <Button
                          color="info"
                          simple
                          size="sm"
                          style={{ fontSize: "0.75rem", padding: "4px 8px" }}
                          onClick={() => history.push("/trainee/dormitory")}
                          title="ไปที่ผังห้องพักหอพัก"
                        >
                          ผังห้องพัก
                        </Button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 3. Building Financial Breakdown (ผลการจัดเก็บรายตึก 1, 2, 3, 4) */}
      <div className="mb-4">
        <h5 style={{ fontWeight: "800", color: "#0f172a", marginBottom: "14px", fontSize: "1.05rem" }}>
          🏢 ประสิทธิภาพการจัดเก็บรายได้ค่าหอพักรายอาคาร (4 อาคาร)
        </h5>
        <GridContainer>
          {(financialData.buildingStats || []).map(b => (
            <GridItem xs={12} sm={6} md={3} key={b.buildingNo}>
              <div style={{ backgroundColor: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h6 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "0.95rem" }}>
                    🏢 {b.buildingName}
                  </h6>
                  <span style={{ fontSize: "0.8rem", fontWeight: "800", color: b.ratePercent >= 80 ? "#166534" : "#b45309" }}>
                    {b.ratePercent}%
                  </span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "10px" }}>
                  💡 {b.rateDesc}
                </div>

                <div className="progress mb-3" style={{ height: "8px", borderRadius: "4px", backgroundColor: "#e2e8f0" }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${b.ratePercent}%`, borderRadius: "4px" }}
                  />
                </div>

                <div style={{ fontSize: "0.82rem", lineHeight: "1.6" }}>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">ยอดเรียกเก็บ:</span>
                    <strong>{(b.expected || 0).toLocaleString()} บ.</strong>
                  </div>
                  <div className="d-flex justify-content-between" style={{ color: "#166534" }}>
                    <span>เก็บได้แล้ว:</span>
                    <strong>{(b.collected || 0).toLocaleString()} บ.</strong>
                  </div>
                  <div className="d-flex justify-content-between" style={{ color: (b.overdue || 0) > 0 ? "#dc2626" : "#64748b" }}>
                    <span>ค้างชำระ:</span>
                    <strong>{(b.overdue || 0).toLocaleString()} บ.</strong>
                  </div>
                  <div className="d-flex justify-content-between text-muted" style={{ borderTop: "1px dashed #e2e8f0", marginTop: "6px", paddingTop: "4px" }}>
                    <span>ผู้พัก / ค้าง:</span>
                    <span>{b.residents || 0} คน / <span style={{ color: (b.overdueCount || 0) > 0 ? "#dc2626" : "#166534", fontWeight: "bold" }}>{b.overdueCount || 0} คน</span></span>
                  </div>
                </div>
              </div>
            </GridItem>
          ))}
        </GridContainer>
      </div>

      {/* 4. Monthly Cash Flow & Interactive Proration Simulator */}
      <GridContainer>
        {/* Monthly Cash Flow Table */}
        <GridItem xs={12} md={7}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "14px", border: "1px solid #cbd5e1", padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <h6 style={{ margin: "0 0 12px", fontWeight: "800", color: "#0f172a", fontSize: "1rem" }}>
              📊 แนวโน้มการจัดเก็บค่าหอพักรายเดือน ปีงบประมาณ {selectedYear}
            </h6>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f1f5f9", color: "#334155", borderBottom: "2px solid #cbd5e1" }}>
                    <th style={{ padding: "8px 10px", textAlign: "center" }}>เดือน</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>ยอดเรียกเก็บ (บ.)</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", color: "#166534" }}>จัดเก็บได้ (บ.)</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", color: "#dc2626" }}>ค้างชำระ (บ.)</th>
                    <th style={{ padding: "8px 10px", textAlign: "center" }}>อัตราจัดเก็บ</th>
                  </tr>
                </thead>
                <tbody>
                  {(financialData.monthlyCashFlow || []).map(m => {
                    const pct = m.expected > 0 ? Math.round((m.collected / m.expected) * 100) : 100;
                    return (
                      <tr key={m.month} style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "6px 10px", textAlign: "center", fontWeight: "700" }}>{m.month}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right" }}>{(m.expected || 0).toLocaleString()}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", color: "#166534", fontWeight: "700" }}>{(m.collected || 0).toLocaleString()}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right", color: (m.overdue || 0) > 0 ? "#dc2626" : "#64748b", fontWeight: "700" }}>{(m.overdue || 0).toLocaleString()}</td>
                        <td style={{ padding: "6px 10px", textAlign: "center" }}>
                          <span style={{ backgroundColor: pct >= 80 ? "#dcfce7" : pct >= 50 ? "#fef3c7" : "#fee2e2", color: pct >= 80 ? "#166534" : pct >= 50 ? "#b45309" : "#991b1b", padding: "2px 6px", borderRadius: "4px", fontSize: "0.76rem", fontWeight: "700" }}>
                            {pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </GridItem>

        {/* Live Proration Simulator Widget */}
        <GridItem xs={12} md={5}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "14px", border: "1px solid #cbd5e1", padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <h6 style={{ margin: "0 0 8px", fontWeight: "800", color: "#065f46", fontSize: "1rem" }}>
              🧮 เครื่องคำนวณและจำลองค่าหอพัก (Proration Calculator)
            </h6>
            <p style={{ margin: "0 0 12px", fontSize: "0.82rem", color: "#64748b" }}>
              ทดสอบคำนวณค่าหอพักตามสูตรเศษสัปดาห์/วัน และตรวจสอบยอดคงค้าง
            </p>

            <div className="mb-2">
              <label className="form-label font-weight-bold" style={{ fontSize: "0.82rem" }}>เลือกอาคาร / เรทราคา</label>
              <select
                className="form-control"
                style={{ fontSize: "0.85rem" }}
                value={simBuilding}
                onChange={(e) => setSimBuilding(e.target.value)}
              >
                <option value="ตึก 1">ตึก 1, 3, 4 (500 บ./ด., สัปดาห์ละ 125, วันละ 17.86 บ.)</option>
                <option value="ตึก 2">ตึก 2 แพทย์ Elective (100 บาท / คน / วัน)</option>
              </select>
            </div>

            {simBuilding === "ตึก 2" ? (
              <div className="mb-2">
                <label className="form-label font-weight-bold" style={{ fontSize: "0.82rem" }}>จำนวนวันเข้าพักจริง (วัน)</label>
                <input
                  type="number"
                  className="form-control"
                  value={simDoctorDays}
                  onChange={(e) => setSimDoctorDays(Math.max(1, Number(e.target.value)))}
                />
              </div>
            ) : (
              <div className="row g-2 mb-2">
                <div className="col-4">
                  <label className="form-label font-weight-bold" style={{ fontSize: "0.78rem" }}>เดือน (x500)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={simMonths}
                    onChange={(e) => setSimMonths(Math.max(0, Number(e.target.value)))}
                  />
                </div>
                <div className="col-4">
                  <label className="form-label font-weight-bold" style={{ fontSize: "0.78rem" }}>สัปดาห์ (x125)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={simWeeks}
                    onChange={(e) => setSimWeeks(Math.max(0, Number(e.target.value)))}
                  />
                </div>
                <div className="col-4">
                  <label className="form-label font-weight-bold" style={{ fontSize: "0.78rem" }}>วัน (x17.86)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={simDays}
                    onChange={(e) => setSimDays(Math.max(0, Number(e.target.value)))}
                  />
                </div>
              </div>
            )}

            {/* Calculated Fee Box */}
            {(() => {
              const isB2 = simBuilding.includes("2");
              const totalDue = isB2
                ? simDoctorDays * 100
                : (simMonths * 500) + (simWeeks * 125) + Math.round(simDays * 17.86 * 100) / 100;
              const paid = Number(simPaidAmount) || 0;
              const remaining = Math.max(0, totalDue - paid);
              const isPaid = remaining === 0 && paid > 0;
              const isPartial = paid > 0 && remaining > 0;

              return (
                <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px", marginTop: "10px" }}>
                  <div style={{ fontSize: "0.8rem", color: "#065f46", fontWeight: "700" }}>
                    สูตรคำนวณ: {isB2 ? `${simDoctorDays} วัน × 100 บ./วัน` : `${simMonths} เดือน (${simMonths * 500}) + ${simWeeks} สัปดาห์ (${simWeeks * 125}) + ${simDays} วัน (${(simDays * 17.86).toFixed(2)})`}
                  </div>
                  <div style={{ fontSize: "1.4rem", fontWeight: "800", color: "#047857", marginTop: "4px" }}>
                    ยอดที่ต้องจ่าย: {totalDue.toLocaleString()} บาท
                  </div>

                  <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #86efac" }}>
                    <label className="form-label font-weight-bold" style={{ fontSize: "0.8rem" }}>
                      ทดสอบกรอกยอดเงินที่จ่ายมา (บาท):
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={simPaidAmount}
                      onChange={(e) => setSimPaidAmount(e.target.value)}
                      placeholder="เช่น 500, 625"
                    />

                    <div className="mt-2" style={{ fontSize: "0.88rem", fontWeight: "700" }}>
                      {isPaid ? (
                        <span style={{ color: "#166534" }}>✓ ชำระครบถ้วนพอดี (ค้าง 0 บาท)</span>
                      ) : isPartial ? (
                        <span style={{ color: "#b45309" }}>⚠️ จ่ายมาไม่ครบ ยังเหลือค้างอีก {remaining.toLocaleString()} บาท</span>
                      ) : (
                        <span style={{ color: "#dc2626" }}>⚠️ ยังไม่จ่าย ค้างชำระเต็ม {remaining.toLocaleString()} บาท</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </GridItem>
      </GridContainer>
    </div>
  )}

      {/* 7. Quick Navigation to 3 Management Modules (Only visible to IT and Research staff) */}
      {canManage && (
        <div className="mb-4">
          <h5 style={{ fontWeight: "800", color: "#0f172a", marginBottom: "16px", fontSize: "1.1rem" }}>
            ⚡ เมนูจัดการข้อมูลระบบ (Trainee Management Modules)
          </h5>
          <GridContainer>
            <GridItem xs={12} md={4}>
              <div className={classes.actionNavCard} onClick={() => history.push("/trainee/list")}>
                <div className="d-flex align-items-center">
                  <div style={{ backgroundColor: "#e0f2fe", border: "1px solid #7dd3fc", color: "#0369a1", borderRadius: "14px", padding: "14px", marginRight: "16px" }}>
                    <SchoolIcon style={{ fontSize: "2.2rem" }} />
                  </div>
                  <div>
                    <h6 style={{ margin: "0 0 4px", fontWeight: "800", color: "#0369a1", fontSize: "1rem" }}>
                      1. ทะเบียนแพทย์และนักศึกษา
                    </h6>
                    <p style={{ margin: 0, fontSize: "0.84rem", color: "#334155", fontWeight: "600", lineHeight: "1.4" }}>
                      บันทึกประวัติแพทย์/นักศึกษา, ช่วงเวลาฝึก, และตารางปฏิทิน Matrix รายเดือน
                    </p>
                  </div>
                </div>
              </div>
            </GridItem>

            <GridItem xs={12} md={4}>
              <div className={classes.actionNavCard} onClick={() => history.push("/trainee/dormitory")}>
                <div className="d-flex align-items-center">
                  <div style={{ backgroundColor: "#ffedd5", border: "1px solid #fdba74", color: "#c2410c", borderRadius: "14px", padding: "14px", marginRight: "16px" }}>
                    <HotelIcon style={{ fontSize: "2.2rem" }} />
                  </div>
                  <div>
                    <h6 style={{ margin: "0 0 4px", fontWeight: "800", color: "#9a3412", fontSize: "1rem" }}>
                      2. ผังห้องพักหอพัก 4 อาคาร
                    </h6>
                    <p style={{ margin: 0, fontSize: "0.84rem", color: "#334155", fontWeight: "600", lineHeight: "1.4" }}>
                      ผังห้องพักตึกละ 3 ชั้น, จัดสรรห้องพัก, และสถานะการเข้าพัก
                    </p>
                  </div>
                </div>
              </div>
            </GridItem>

            <GridItem xs={12} md={4}>
              <div className={classes.actionNavCard} onClick={() => history.push("/trainee/master-data")}>
                <div className="d-flex align-items-center">
                  <div style={{ backgroundColor: "#f3e8ff", border: "1px solid #d8b4fe", color: "#6b21a8", borderRadius: "14px", padding: "14px", marginRight: "16px" }}>
                    <StorageIcon style={{ fontSize: "2.2rem" }} />
                  </div>
                  <div>
                    <h6 style={{ margin: "0 0 4px", fontWeight: "800", color: "#581c87", fontSize: "1rem" }}>
                      3. จัดการข้อมูลหลัก (Master Data)
                    </h6>
                    <p style={{ margin: 0, fontSize: "0.84rem", color: "#334155", fontWeight: "600", lineHeight: "1.4" }}>
                      จัดการเลขห้องพักแต่ละตึก/ชั้น, รายชื่อสถาบัน, และกลุ่มงาน รพ.
                    </p>
                  </div>
                </div>
              </div>
            </GridItem>
          </GridContainer>
        </div>
      )}

      {/* Modal: Record Payment & Balance Calculation */}
      <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setPaymentModal(false)} style={{ backgroundColor: "#064e3b", color: "#ffffff" }}>
          💳 บันทึกรับชำระเงินและคำนวณยอดค้างชำระค่าหอพัก
        </ModalHeader>
        <ModalBody>
          {selectedStayForPayment && (
            <div>
              {/* Trainee Info Card */}
              <div style={{ backgroundColor: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: "800", fontSize: "1.1rem", color: "#0f172a" }}>
                      {selectedStayForPayment.name || selectedStayForPayment.traineeName}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#475569", marginTop: "2px" }}>
                      <strong>ตำแหน่ง/สังกัด:</strong> {selectedStayForPayment.role || selectedStayForPayment.position || "-"} • {selectedStayForPayment.institution || "-"}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#334155", marginTop: "2px" }}>
                      <strong>ห้องพัก / ช่วงเวลา:</strong> {selectedStayForPayment.room || `${selectedStayForPayment.buildingName} ห้อง ${selectedStayForPayment.roomNumber}`} ({selectedStayForPayment.dateRange})
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee and Payment Inputs */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                    ยอดค่าหอพักทั้งหมด (Total Due) *
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#0f172a" }}
                      value={paymentInput.feeAmount}
                      onChange={(e) => {
                        const newFee = Number(e.target.value) || 0;
                        const paid = Number(paymentInput.paidAmount) || 0;
                        const rem = Math.max(0, newFee - paid);
                        let autoStatus = "OVERDUE";
                        if (rem === 0 && newFee > 0) autoStatus = "PAID";
                        else if (paid > 0 && rem > 0) autoStatus = "PARTIAL";
                        setPaymentInput({
                          ...paymentInput,
                          feeAmount: e.target.value,
                          paymentStatus: autoStatus
                        });
                      }}
                      placeholder="เช่น 500, 1500, 1600"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">บาท</span>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label className="form-label font-weight-bold" style={{ color: "#065f46" }}>
                      ยอดเงินที่ชำระแล้วจริง (Paid Amount) *
                    </label>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-success"
                        style={{ fontSize: "0.75rem", padding: "2px 6px", fontWeight: "bold" }}
                        onClick={() => {
                          const fee = Number(paymentInput.feeAmount) || 0;
                          setPaymentInput({
                            ...paymentInput,
                            paidAmount: fee,
                            paymentStatus: fee > 0 ? "PAID" : "FREE"
                          });
                        }}
                      >
                        ⚡ ชำระครบ
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary"
                        style={{ fontSize: "0.75rem", padding: "2px 6px" }}
                        onClick={() => {
                          setPaymentInput({
                            ...paymentInput,
                            paidAmount: 0,
                            paymentStatus: "OVERDUE"
                          });
                        }}
                      >
                        🔄 ยังไม่จ่าย (0)
                      </button>
                    </div>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#065f46" }}
                      value={paymentInput.paidAmount}
                      onChange={(e) => {
                        const paid = Number(e.target.value) || 0;
                        const fee = Number(paymentInput.feeAmount) || 0;
                        const rem = Math.max(0, fee - paid);
                        let autoStatus = "OVERDUE";
                        if (rem === 0 && fee > 0) autoStatus = "PAID";
                        else if (paid > 0 && rem > 0) autoStatus = "PARTIAL";
                        setPaymentInput({
                          ...paymentInput,
                          paidAmount: e.target.value,
                          paymentStatus: autoStatus
                        });
                      }}
                      placeholder="เช่น 500, 1000, 1500"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">บาท</span>
                    </div>
                  </div>
                  <small className="text-muted">กรอกยอดเงินที่น้องโอนหรือชำระเข้ามาจริง</small>
                </div>
              </div>

              {/* Live Balance Deduction Display */}
              {(() => {
                const totalDue = Number(paymentInput.feeAmount) || 0;
                const paid = Number(paymentInput.paidAmount) || 0;
                const remaining = Math.max(0, totalDue - paid);

                return (
                  <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#475569", marginBottom: "8px" }}>
                      📊 สรุปการหักลบยอดเงิน (Realtime Calculation):
                    </div>
                    <div className="row text-center mb-2">
                      <div className="col-4">
                        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>ยอดทั้งหมด</div>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#0f172a" }}>
                          {totalDue.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                      <div className="col-4" style={{ borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "0.8rem", color: "#166534" }}>หักลบจ่ายแล้ว</div>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#166534" }}>
                          {paid.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                      <div className="col-4">
                        <div style={{ fontSize: "0.8rem", color: remaining > 0 ? "#dc2626" : "#64748b" }}>ยอดที่ยังไม่จ่าย (ค้างชำระ)</div>
                        <div style={{ fontSize: "1.25rem", fontWeight: "900", color: remaining > 0 ? "#dc2626" : "#059669" }}>
                          {remaining.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                    </div>

                    {remaining === 0 && totalDue > 0 ? (
                      <div style={{ backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <CheckCircleIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ชำระครบถ้วนสมบูรณ์แล้ว (ยอดค้างชำระ 0 บาท)
                      </div>
                    ) : remaining > 0 && paid > 0 ? (
                      <div style={{ backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <WarningIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ⚠️ จ่ายบางส่วน (จ่ายไม่ครบ) ยังเหลือค้างชำระอีก <strong>{remaining.toLocaleString()} บาท</strong>
                      </div>
                    ) : (
                      <div style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <WarningIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ⚠️ ยังไม่ชำระ มียอดค้างชำระเต็มจำนวน <strong>{remaining.toLocaleString()} บาท</strong>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Status and Details */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label font-weight-bold">สถานะการชำระเงิน *</label>
                  <select
                    className="form-control"
                    value={paymentInput.paymentStatus}
                    onChange={(e) => setPaymentInput({ ...paymentInput, paymentStatus: e.target.value })}
                  >
                    <option value="PAID">✅ ชำระครบแล้ว (PAID)</option>
                    <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                    <option value="PARTIAL">⚠️ ชำระบางส่วน / จ่ายไม่ครบ (PARTIAL)</option>
                    <option value="OVERDUE">❌ ค้างชำระ / ยังไม่จ่าย (OVERDUE)</option>
                    <option value="FREE">🆓 ยกเว้นค่าธรรมเนียม (FREE)</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label font-weight-bold">วันที่รับชำระ (พ.ศ.)</label>
                    {paymentInput.paidDate && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                        onClick={() => setPaymentInput({ ...paymentInput, paidDate: "" })}
                      >
                        ล้างวันที่
                      </button>
                    )}
                  </div>
                  <Datetime
                    value={paymentInput.paidDate && moment(paymentInput.paidDate).isValid() ? moment(paymentInput.paidDate) : null}
                    renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                    inputProps={{
                      placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                      readOnly: "readonly",
                      style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                      className: "form-control",
                      value: paymentInput.paidDate && moment(paymentInput.paidDate).isValid() ? moment(paymentInput.paidDate).format("D MMMM BBBB") : ""
                    }}
                    closeOnSelect={true}
                    closeOnTab={true}
                    dateFormat="D MMMM BBBB"
                    timeFormat={false}
                    onChange={(val) => {
                      if (moment.isMoment(val) && val.isValid()) {
                        setPaymentInput({ ...paymentInput, paidDate: val.format("YYYY-MM-DD") });
                      } else if (val && typeof val === "string") {
                        const m = moment(val);
                        if (m.isValid()) setPaymentInput({ ...paymentInput, paidDate: m.format("YYYY-MM-DD") });
                      } else if (!val) {
                        setPaymentInput({ ...paymentInput, paidDate: "" });
                      }
                    }}
                  />
                </div>

                {/* ซ่อนเลขที่ใบเสร็จรับเงินไว้ก่อนตามที่ผู้ใช้ร้องขอ (เก็บ state ไว้ในระบบ) */}
                {/*
                <div className="col-md-4">
                  <label className="form-label font-weight-bold">เลขที่ใบเสร็จรับเงิน</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น REC-2569-11-001"
                    value={paymentInput.receiptNo}
                    onChange={(e) => setPaymentInput({ ...paymentInput, receiptNo: e.target.value })}
                  />
                </div>
                */}
              </div>

              {/* Reason / Remarks Field */}
              <div className="mb-2">
                <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                  เหตุผล / หมายเหตุการชำระเงิน
                  <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b", marginLeft: "6px" }}>
                    (ระบุรายละเอียด เช่น จ่ายงวดแรก 500 บาท ที่เหลือจะจ่ายสิ้นเดือน, ขอผ่อนผัน ฯลฯ)
                  </span>
                </label>
                <textarea
                  rows={2}
                  className="form-control"
                  placeholder="เช่น ทยอยจ่ายงวดแรก 500 บาท ส่วนที่เหลือ 1,000 บาท จะโอนเพิ่มสิ้นเดือนนี้"
                  value={paymentInput.note}
                  onChange={(e) => setPaymentInput({ ...paymentInput, note: e.target.value })}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPaymentModal(false)}>ยกเลิก</Button>
          <Button color="success" onClick={handleSavePayment}>💾 บันทึกรับชำระเงิน</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
